// San Francisco, "Fog over the Golden Gate". A clear, starry night from the top of a steep Russian Hill
// street: the Golden Gate Bridge's orange towers rise out of a fog bank that pours in through the Gate
// and spills over the Marin Headlands like a slow waterfall.
// Back to front: stars, a crescent moon, a meteor and a plane -> Mt Tam -> the billowing Pacific fog bank
// -> the bay -> Marin Headlands with the fog cascade -> the bridge (floodlit towers, cables, deck lamps,
// traffic, aviation beacons) -> an outbound container ship -> the fog tongue under the deck (foghorn glow)
// -> Alcatraz and its sweeping lighthouse -> sailboats -> the waterfront (curved pier, a tall ship, sea
// lions) and the lower city -> a lamplit rooftop billboard on the last Victorian (its face is the exported
// `billboard`; render(t, { pitch }) swaps its crab-shack ad for the site's pitch) -> painted Victorians stepping
// down the hill -> the street, its cable car tracks and a globe streetlamp -> cable cars -> a dog walker and a couple
// strolling down to the view -> fog wisps and fine drizzle glinting in the light -> vignette.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const S0 = 186;                                   // back edge of the far sidewalk at x = 0 (the crest)
const sw = x => S0 + Math.floor(x / 4);           // the street drops 1 px every 4 px toward the bay (right)
const HOUSE_W = 44, ROW_END = 4 * HOUSE_W;        // four painted Victorians step down the hill (x 0..176)
const RAIL_F = 12, RAIL_N = 22;                   // wheel lines below sw(x): uphill (far) and downhill (near) track
const LAMP = { x: 198, y: 186 };                  // globe of the streetlamp at the foot of the row
const BILL = { x: 130, y: 101, w: 48, h: 20 };    // face of the rooftop billboard on the last Victorian (exported as `billboard`)
const DECK = 152;                                 // bridge roadway
// the towers rise about a third of their spacing above the roadway (real: ~152 m over a 1,280 m main span, seen
// nearly broadside from Russian Hill; a little taller here so the four portals still read), so the main cables hang
// in a long, shallow drape that nearly meets the deck at mid-span. That oblique view also slims the portal faces
const T0 = { cx: 214, top: 106, hw: 6, lw: 4 };   // south tower (nearer, taller)
const T1 = { cx: 366, top: 112, hw: 5, lw: 3 };   // north tower, at the foot of the headlands
const WF = 213;                                   // waterfront: the bay above, the lower city below
const MOON = { x: 382, y: 30 };
const ALC = { x: 400, y: 170 };                   // Alcatraz sprite origin
const LH = { x: ALC.x + 12, y: ALC.y + 4 };       // Alcatraz lighthouse lantern
const FOGHORNS = [12, 60, 108, 156, 204];         // the bridge foghorn, every 48 s
const SHIP = { t0: 44, t1: 140, x0: 486, x1: 226, wl0: 207, wl1: 185 };  // outbound container ship (bow = left end)
const SHIP_HORN = 97;                             // the ship sounds as it enters the fog

/* ================= Pixel helpers ================= */
function painter(w, h) {                          // ImageData painter for pixel-heavy layers
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function line(c, x0, y0, x1, y1, col) {           // integer Bresenham line
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
/* crop a full-screen layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
const ridgeAt = (pts, x) => {                     // linear interpolation through [[x, y], ...]
  if (x <= pts[0][0]) return pts[0][1];
  for (let k = 1; k < pts.length; k++) if (x <= pts[k][0]) {
    const [a, ya] = pts[k - 1], [b, yb] = pts[k];
    return ya + (yb - ya) * (x - a) / (b - a);
  }
  return pts[pts.length - 1][1];
};
/* velocity profile with eased ends: accel over the first ea, decel over the last ed of u in [0, 1] */
function cruise(u, ea, ed) {
  const v = 1 / (1 - ea / 2 - ed / 2);
  if (u < ea) return v * u * u / (2 * ea);
  if (u < 1 - ed) return v * (ea / 2 + u - ea);
  const r = 1 - u;
  return 1 - v * r * r / (2 * ed);
}
/* a warm light pool tint baked into static layers: [cx, cy, rx, ry, colour, strength] */
const tintCache = {};
function tint(col, tcol, a) {
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
const POOLS = [[LAMP.x, 214, 70, 46, '#ffb466', 0.55], [LAMP.x, 244, 64, 16, '#ffb466', 0.6]];
function lit(i, y, col, pools = POOLS) {
  for (const [cx, cy, rx, ry, lc, s] of pools) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.62, q * 0.75));
  }
  return col;
}

/* ================= Sky: clear, deep blue, lighter toward the fog ================= */
const SKY = ['#02041a', '#040820', '#060c28', '#091230', '#0c1738', '#101d42', '#15244c', '#1b2b55',
  '#22335d', '#2b3c64', '#35466c'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 150), 1.3) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 120, (y - MOON.y) / 70)) * 0.22;   // moon glow
    v += Math.max(0, 1 - Math.hypot((i - 300) / 280, (y - 140) / 50)) * 0.14;         // fog shine
    return v;
  });
}
const STARS = [];
{
  const r = rng(415), cols = ['#e6ecff', '#e6ecff', '#fff0d8', '#c8d8ff'];
  while (STARS.length < 120) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(Math.pow(r(), 1.3) * 118);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 15) continue;
    const b = (0.22 + Math.pow(r(), 2.4) * 0.78) * (1 - smooth(70, 122, y) * 0.6);
    STARS.push({ x, y, b, k: 20 + Math.floor(r() * 80), ph: r(), col: cols[Math.floor(r() * 4)], big: b > 0.72 });
  }
}
function renderMoon() {                           // waxing crescent with a faint earthshine disc
  const r = 7, s = r * 2 + 3, p = painter(s, s), c0 = r + 1;
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    const inner = Math.hypot(dx + 4.4, dy + 1.2);
    if (inner > r - 0.2) p.put(i, y, dd > r - 1 ? (dy > 2 ? '#c8c0a8' : '#efe6c8') : inner > r + 1.4 ? '#fffbea' : '#f4ecd2');
    else p.put(i, y, '#141c3a');
  }
  return p.done();
}
const METEOR = { t0: 37, t1: 38.1, x0: 172, y0: 12, x1: 116, y1: 42 };
function drawMeteor(t) {
  if (t < METEOR.t0 || t >= METEOR.t1) return;
  const u = (t - METEOR.t0) / (METEOR.t1 - METEOR.t0), fade = 1 - smooth(0.7, 1, u);
  ctx.fillStyle = '#eef2ff';
  for (let k = 0; k < 12; k++) {
    const b = u - k * 0.028;
    if (b < 0) break;
    ctx.globalAlpha = fade * (1 - k / 12) * (k === 0 ? 1 : 0.7);
    ctx.fillRect(Math.round(lerp(METEOR.x0, METEOR.x1, b)), Math.round(lerp(METEOR.y0, METEOR.y1, b)), 1, 1);
  }
  ctx.globalAlpha = 1;
}
const PLANE = { t0: 162, t1: 218 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(492 - u * 506), y = Math.round(44 - u * 8);
  rect(ctx, x, y, 4, 1, '#1a2036'); px(ctx, x + 2, y - 1, '#1a2036');
  px(ctx, x - 1, y, '#fff6e0'); halo(HL.head, x - 1, y, 0.45);
  if (frac(t / 1.3) < 0.09) { px(ctx, x + 2, y, '#ffffff'); halo(HL.strobe, x + 2, y, 0.8); }
  if (frac(t / 1.6 + 0.3) < 0.5) px(ctx, x + 3, y - 1, '#ff3a3a');
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillStyle = s.col; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.35; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9 + 0.1 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  drawMeteor(t);
  drawPlane(t);
}

/* ================= Marin: Mt Tam behind, the headlands in front, Angel Island ================= */
const TAM = [[318, 134], [350, 121], [380, 109], [408, 98], [434, 89], [450, 86], [464, 89], [480, 95]];
const HEAD = [[338, 176], [346, 167], [354, 155], [362, 145], [370, 137], [380, 131], [390, 128], [400, 127],
  [410, 125], [420, 119], [432, 112], [444, 107], [456, 105], [468, 107], [480, 110]];
const ANGEL = [[438, 178], [448, 170], [460, 165], [472, 163], [480, 164]];
const SHORE_Y = 174;
function renderTam() {
  const p = painter(W, H);
  for (let x = 318; x < W; x++) {
    const top = Math.round(ridgeAt(TAM, x) + (hash(x >> 1, 3, 71) - 0.5) * 1.4);
    for (let y = top; y < 150; y++) p.put(x, y, y === top ? '#2c3860' : y === top + 1 ? '#1e2848' : (bayer(x, y) < 0.3 ? '#161f3c' : '#131a34'));
  }
  return p.done();
}
const MARIN_LIGHTS = [[441, 131, 0], [452, 137, 1], [463, 124, 0], [472, 141, 0], [458, 151, 1], [447, 160, 0],
  [466, 171, 0], [476, 169, 1], [T1.cx + T1.hw + 3, 170, 2]];                    // green: the Lime Point light by the north tower
function renderHeadlands() {
  const p = painter(W, H);
  for (let x = 338; x < W; x++) {
    const top = Math.round(ridgeAt(HEAD, x) + (hash(x >> 1, 5, 72) - 0.5) * 1.6);
    const shore = SHORE_Y + Math.round(hash(x >> 2, 6, 72) * 2);
    for (let y = top; y <= shore; y++) {
      let col = (hash(x >> 1, y >> 1, 73) + bayer(x, y) * 0.5) < 0.62 ? '#0c1226' : '#10172e';
      const k = y - top;
      if (k > 4 && frac((x + k * 0.9) / 13 + hash(x >> 3, 1, 74) * 0.2) < 0.1) col = '#080c1c';   // gullies
      if (k === 0) col = '#36426c'; else if (k === 1) col = '#222c4e'; else if (k === 2 && bayer(x, y) < 0.5) col = '#182140';
      if (y >= shore - 1) col = (x + y) & 1 ? '#161c32' : '#0a0e1e';                              // rocks at the waterline
      p.put(x, y, col);
    }
  }
  for (let x = 438; x < W; x++) {                                                                // Angel Island, nearer
    const top = Math.round(ridgeAt(ANGEL, x) + 0.8 * Math.sin(x * 0.55) + (hash(x >> 1, 9, 72) - 0.5) * 1.5);
    for (let y = top; y <= 179; y++) p.put(x, y, y === top ? '#222c4c' : y > 177 ? '#141a30' : (bayer(x, y) < 0.4 || hash(x >> 1, y >> 1, 76) < 0.2) ? '#0e1328' : '#0b1022');
  }
  return p.done();
}

/* ================= The Pacific fog bank beyond the Gate =================
   Tileable strips of merged elliptical billows (moonlit rims, solid below), scrolling east at two speeds.
   The Marin strip sits higher, piled against the headlands, and fades out toward the Gate. */
const BANK_DEFS = [
  { seed: 11, y: 108, h: 46, n: 10, k: 1, rx: [16, 34], ry: [5, 11], base: 0.52, pal: { top: '#a2aecd', top2: '#8896ba', body: '#6e7ca4', core: '#6a78a0' } },
  { seed: 23, y: 131, h: 25, n: 12, k: 2, rx: [12, 26], ry: [3, 6], base: 0.42, pal: { top: '#8c9abe', top2: '#8090b4', body: '#7280a8', core: '#6e7ca4' } },
  { seed: 37, y: 92, h: 60, n: 11, k: 1, rx: [12, 26], ry: [4, 9], base: 0.3, pal: { top: '#a2aecc', top2: '#8a98bc', body: '#6e7ca4', core: '#66749c' } },
];
function makeBankLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.n; b++) {
    const bx = (b + r() * 0.7) * W / def.n, np = 3 + Math.floor(r() * 3);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * 34, y: h * def.base - r() * ry * 1.1, rx, ry });
    }
  }
  const D = new Float32Array(W * h), base = h * def.base;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = y > base ? 0.6 : -9;                                   // solid below the billows
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y, v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (v > best) best = v;
    }
    D[y * W + x] = best + 0.12 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
  }
  const get = (x, y) => y < 0 ? -9 : y >= h ? 1 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y - 2) + th <= 0.03 || (get(x, y - 3) <= 0.03 && bayer(x, y) < 0.5)) col = P.top2;
    else if (v > 0.5 + th && y < base) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k, h };
}
function renderFogBody() {                       // the bank's body sitting on the strait, down to the tower footings
  return ditherFill(W, 190 - 148, ['#6e7ca4', '#6a78a0', '#66749c', '#5e6c94', '#56648c'], (i, y) => Math.pow(y / 42, 1.4));
}
function renderMarinMask() {                      // the Marin strip thins out toward the Gate
  const w = W - 340, h = BANK_DEFS[2].h, p = painter(w, h);
  for (let x = 0; x < w; x++) for (let y = 0; y < h; y++) {
    const a = smooth(344, 380, 340 + x);
    const q = Math.floor(a * 6 + bayer(x, y)) / 6;
    if (q > 0) p.put(x, y, '#ffffff', Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
function drawBank(t) {
  const [L1, L2, LM] = BANKS;
  const [sc, sx] = MARIN_SCR, mo = scroll(t, LM.k, W);
  sx.globalCompositeOperation = 'source-over';
  sx.clearRect(0, 0, sc.width, sc.height);
  sx.drawImage(LM.c, mo - W - 340, 0); sx.drawImage(LM.c, mo - 340, 0);
  sx.globalCompositeOperation = 'destination-in'; sx.drawImage(MARIN_MASK, 0, 0);
  sx.globalCompositeOperation = 'source-over';
  ctx.drawImage(sc, 340, LM.y);
  ctx.drawImage(FOG_BODY, 0, 148);
  for (const L of [L1, L2]) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
}

/* ================= The fog cascade: a sheet flowing down the headland's saddle (masked, scrolling) ================= */
const CAS = { x: 372, y: 110, w: 58, h: 68, tile: 48 };
function renderCascadeTex() {
  const p = painter(CAS.w, CAS.tile);
  for (let y = 0; y < CAS.tile; y++) for (let x = 0; x < CAS.w; x++) {
    const a = TAU * y / CAS.tile;
    const v = 0.56 + 0.22 * Math.sin(TAU * x / 17 + 2.2 * Math.sin(a) + 0.8) + 0.14 * Math.sin(TAU * x / 7.3 - 2 * a + 1.3 * Math.sin(2 * a))
      + 0.1 * Math.sin(3 * a + x * 0.37);
    const q = Math.floor((0.55 + 0.45 * clamp(v, 0, 1)) * 4 + bayer(x, y)) / 4;
    const c = v + (bayer(x, y) - 0.5) * 0.2;
    if (q > 0) p.put(x, y, c > 0.72 ? '#b0bcd8' : c > 0.46 ? '#94a2c4' : '#7a88ae', Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
function renderCascadeMask() {
  const p = painter(CAS.w, CAS.h);
  for (let x = 0; x < CAS.w; x++) {
    const X = CAS.x + x, r = ridgeAt(HEAD, X), lat = smooth(373, 388, X) * (1 - smooth(409, 429, X));
    for (let y = 0; y < CAS.h; y++) {
      const Y = CAS.y + y;
      if (Y < r - 2) continue;
      let a = Y < r + 3 ? 1 : lerp(0.95, 0.5, smooth(r + 3, 162, Y)) + 0.45 * smooth(156, 170, Y);
      a *= lat * (1 - smooth(SHORE_Y - 1, SHORE_Y + 3, Y));
      const q = Math.floor(clamp(a, 0, 1) * 6 + bayer(x, y)) / 6;
      if (q > 0) p.put(x, y, '#ffffff', Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
function drawCascade(t) {
  const [sc, sx] = CAS_SCR;
  sx.globalCompositeOperation = 'source-over';
  sx.clearRect(0, 0, CAS.w, CAS.h);
  const off = scroll(t, 15, CAS.tile);                        // 3 px/s downhill
  for (let k = -1; k <= 1; k++) sx.drawImage(CAS_TEX, 0, off + k * CAS.tile);
  sx.globalCompositeOperation = 'destination-in';
  sx.drawImage(CAS_MASK, 0, 0);
  sx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.9; ctx.drawImage(sc, CAS.x, CAS.y); ctx.globalAlpha = 1;
}

/* ================= The bay ================= */
const WATER_TOP = 176;
const renderWater = () => ditherFill(W, WF - WATER_TOP, ['#56648c', '#3c4870', '#2a3456', '#1e2846', '#161e38', '#10172c', '#0c1224', '#0a0f1e'],
  (i, y) => Math.pow(y / (WF - WATER_TOP), 0.45));

/* ================= The Golden Gate Bridge ================= */
// International orange, floodlit from the deck: 5 shades dark -> light
// (the highlights stay red-orange, not yellow, as the real paint does under the floodlights)
const OR = ['#6e2420', '#94301f', '#b83a24', '#da4a2c', '#f2603a', '#ff7c58'];
const DECK_X0 = 150, DECK_X1 = 414;
const SADDLE = T => [T.cx - T.hw + 1, T.cx + T.hw - 1, T.top + 2];   // cable saddles on the two leg tops
const CABLE = (() => {                            // main span parabola between the inner saddles, sagging to the deck
  const xa = SADDLE(T0)[1], ya = SADDLE(T0)[2], xb = SADDLE(T1)[0], yb = SADDLE(T1)[2], yv = DECK - 3;
  const k = Math.sqrt((yv - ya) / (yv - yb)), xv = (xa + k * xb) / (1 + k), a = (yv - ya) / ((xv - xa) ** 2);
  return x => yv - a * (x - xv) ** 2;
})();
const sideCable = (x, xa, ya, xb, yb, sag) => { const u = (x - xa) / (xb - xa); return ya + (yb - ya) * u + sag * 4 * u * (1 - u); };
const SOUTH_SIDE = x => sideCable(x, SADDLE(T0)[0], SADDLE(T0)[2], 168, DECK - 4, 4);
const NORTH_SIDE = x => sideCable(x, SADDLE(T1)[1], SADDLE(T1)[2], DECK_X1 - 4, DECK - 3, 3);
function renderCables() {
  const [c, x] = makeCanvas(W, H);
  const spans = [[168, SADDLE(T0)[0], SOUTH_SIDE], [SADDLE(T0)[1], SADDLE(T1)[0], CABLE], [SADDLE(T1)[1], DECK_X1 - 4, NORTH_SIDE]];
  x.globalAlpha = 0.2;                                           // suspender ropes
  for (const [a, b, f] of spans) for (let gx = a + 2; gx < b - 1; gx += 3) {
    const cy = Math.round(f(gx)) + 2;
    if (DECK - 3 - cy > 0 && Math.abs(gx - T0.cx) > T0.hw + 2 && Math.abs(gx - T1.cx) > T1.hw + 2) rect(x, gx, cy, 1, DECK - 3 - cy, '#dc5a3c');
  }
  x.globalAlpha = 1;
  for (const [a, b, f] of spans) {                               // the main cables: lit top, shaded belly
    let prev = null;
    for (let gx = a; gx <= b; gx++) {
      const yy = Math.round(f(gx));
      if (prev !== null) { line(x, gx - 1, prev + 1, gx, yy + 1, '#a8382a'); line(x, gx - 1, prev, gx, yy, '#ff7a52'); }
      prev = yy;
    }
  }
  return c;
}
const DECK_LAMPS = [];
for (let gx = DECK_X0 + 3; gx < DECK_X1; gx += 7) if (Math.abs(gx - T0.cx) > T0.hw + 1 && Math.abs(gx - T1.cx) > T1.hw + 1) DECK_LAMPS.push(gx);
function renderDeck() {
  const p = painter(W, H);
  for (let gx = DECK_X0; gx < DECK_X1; gx++) {
    p.put(gx, DECK - 2, (gx & 1) ? '#b04a30' : '#8a3626');
    p.put(gx, DECK - 1, '#281c22'); p.put(gx, DECK, '#1e161c');
    p.put(gx, DECK + 1, '#e46036');
    p.put(gx, DECK + 2, (gx & 1) ? '#a2402a' : '#4a1c1c');
    p.put(gx, DECK + 3, (gx % 3 === 0) ? '#d05634' : '#b04830');
    if (gx & 1) p.put(gx, DECK + 4, '#2a1216');
  }
  for (const lx of DECK_LAMPS) { p.put(lx, DECK - 3, '#ffe2a0'); p.put(lx, DECK - 2, '#5a2a22'); }
  for (let y = DECK - 3; y <= DECK + 6; y++) for (let gx = DECK_X1; gx <= DECK_X1 + 7; gx++)   // north anchorage block
    p.put(gx, y, y === DECK - 3 ? '#4e5266' : gx === DECK_X1 ? '#40445a' : gx === DECK_X1 + 7 ? '#1a1d2e' : (y & 3) === 0 ? '#262a3c' : '#2e3246');
  for (let gx = DECK_X1 + 8; gx < W; gx++) {                                                   // the road climbing into Marin
    const y = Math.round(DECK - 1 - (gx - DECK_X1 - 8) * 0.2);
    p.put(gx, y, '#2a2e40'); p.put(gx, y + 1, '#161a2a');
    if ((gx - DECK_X1) % 8 === 4) p.put(gx, y - 1, '#e8c890');
  }
  return p.done();
}
function renderTower(p, T) {
  const { cx, top, hw, lw } = T, hd = DECK - top, x0 = cx - hw, x1 = cx + hw;
  const at = f => top + Math.round(hd * f);
  // a deep crown strut, then three portal struts whose openings grow taller toward the deck, and the strut under it
  const struts = [[top + 2, top + 4], [at(0.24), at(0.24) + 1], [at(0.46), at(0.46) + 1], [at(0.7), at(0.7) + 1], [DECK + 9, DECK + 11]];
  const setY = struts[2][0];                                      // legs step in above the third strut
  const shade = (y, i, xx) => {
    const h = y <= DECK ? (y - top) / hd : 1 - (y - DECK) / 40;
    const b = (y <= DECK ? 0.28 + 0.62 * Math.pow(h, 1.15) : 0.5 * h) * 4 + (bayer(xx, y) - 0.5) * 0.7;
    return clamp(Math.round(b) + i, 0, OR.length - 1);
  };
  for (let y = top; y <= 184; y++) {
    const inset = y < setY ? 1 : 0;
    for (const side of [0, 1]) {                                  // the two legs
      const w = lw - inset;
      for (let k = 0; k < w; k++) {
        const outer = side ? k === w - 1 : k === 0, inner = side ? k === 0 : k === w - 1;
        if (y === top && (outer || (inner && w > 2))) continue;     // stepped Art Deco caps, one pixel at the tip
        if (y === top + 1 && outer && w > 2) continue;
        const xx = side ? x1 - inset - (w - 1 - k) : x0 + inset + k;
        const flute = w > 2 && (side ? k === 1 : k === w - 2);
        const i = outer ? (side ? -1 : 1) : flute ? -1 : 0;
        p.put(xx, y, OR[shade(y, i, xx)]);
      }
    }
    for (const [a, b] of struts) if (y >= a && y <= b) {           // portal struts: shaded top, floodlit underside
      const i = y === b ? 1 : y === a ? -1 : 0;
      for (let xx = x0 + lw; xx <= x1 - lw; xx++) {
        const rib = a === top + 2 && i === 0 && ((xx - cx) & 1) === 0;   // vertical fins on the crown strut
        p.put(xx, y, OR[shade(y, rib ? -1 : i, xx)]);
      }
    }
  }
  return { struts };
}
function renderTowers() {
  const p = painter(W, H);
  renderTower(p, T0); renderTower(p, T1);
  return p.done();
}
const legTops = T => [T.cx - T.hw + 2, T.cx + T.hw - 2];   // the cap pixel on each stepped-in leg
const DECK_CARS = [];
{ const r = rng(21); for (let i = 0; i < 20; i++) DECK_CARS.push({ dir: i % 2 ? -1 : 1, n: 4 + Math.floor(r() * 5), ph: r(), cool: r() < 0.3 }); }
function drawDeckCars(t) {
  const span = DECK_X1 - DECK_X0 + 24;
  for (const car of DECK_CARS) {
    const u = frac(t * car.n / LOOP + car.ph);
    const x = car.dir > 0 ? Math.round(DECK_X0 - 12 + u * span) : Math.round(DECK_X1 + 12 - u * span);
    if (x < DECK_X0 || x > DECK_X1 - 3) continue;
    const y = car.dir > 0 ? DECK : DECK - 1;
    px(ctx, x + 1, y, '#0c0a10');
    px(ctx, car.dir > 0 ? x + 2 : x, y, car.cool ? '#e8f0ff' : '#fff0c8');
    px(ctx, car.dir > 0 ? x : x + 2, y, '#ff3a2c');
    ctx.globalAlpha = 0.35; px(ctx, car.dir > 0 ? x + 3 : x - 1, y, '#fff0c8'); ctx.globalAlpha = 1;
  }
}
function drawBridge(t) {
  for (const T of [T0, T1]) halo(HL.tower, T.cx, DECK - 12, 0.9 + 0.1 * wave(t, 5, T.cx * 0.01));
  ctx.drawImage(CABLES_C.c, CABLES_C.x, CABLES_C.y);
  ctx.drawImage(DECK_C.c, DECK_C.x, DECK_C.y);
  drawDeckCars(t);
  ctx.drawImage(TOWERS_C.c, TOWERS_C.x, TOWERS_C.y);
  for (const T of [T0, T1]) halo(HL.towerTop, T.cx, T.top + 6, 0.7);
  // aviation beacons: the two towers blink alternately, steady reds half way up
  [T0, T1].forEach((T, i) => {
    const on = frac(t / 2 + i * 0.5) < 0.5;
    for (const bx of legTops(T)) { px(ctx, bx, T.top - 1, on ? '#ff4040' : '#5a1616'); if (on) halo(HL.red, bx, T.top - 1, 0.85); }
    const my = T.top + Math.round((DECK - T.top) * 0.45);
    for (const bx of [T.cx - T.hw - 1, T.cx + T.hw + 1]) { px(ctx, bx, my, '#ff5040'); halo(HL.redDim, bx, my, 0.6); }
  });
}
function drawDeckLamps(t) {
  DECK_LAMPS.forEach((lx, i) => halo(HL.deckLamp, lx, DECK - 3, 0.8 + 0.2 * wave(t, 600 + i * 17)));
}

/* ================= Outbound container ship: slides under the bridge into the fog ================= */
function renderShip() {
  const w = 52, h = 15, p = painter(w, h);
  const CONT = ['#5a2a28', '#2a4252', '#4c4c58', '#28345c', '#6a4e28', '#34503a', '#6a3a4a'];
  for (let cx = 10; cx < 37; cx += 4) {                              // container stacks
    const tiers = 1 + Math.floor(hash(cx, 1, 81) * 3);
    for (let tier = 0; tier < tiers; tier++) {
      const col = CONT[Math.floor(hash(cx, tier, 82) * CONT.length)], y = 9 - tier * 2;
      for (let i = 0; i < 3; i++) { p.put(cx + i, y - 1, col); p.put(cx + i, y, tint(col, '#000000', 0.35)); }
      p.put(cx, y - 1, tint(col, '#ffffff', 0.2));
    }
  }
  for (let y = 2; y <= 10; y++) for (let x = 38; x <= 47; x++) p.put(x, y, x === 38 ? '#aab2c6' : y === 2 ? '#b8c0d2' : '#7e869c');   // bridge house
  for (let x = 39; x <= 46; x += 2) { p.put(x, 4, '#ffe0a0'); if (x < 44) p.put(x, 7, '#e8c080'); }
  for (let y = 0; y <= 2; y++) for (let x = 43; x <= 45; x++) p.put(x, y, y === 1 ? '#8a2a26' : '#1a1c26');                           // funnel
  for (let y = 2; y <= 9; y++) p.put(8, y, '#3a4058');                                                                          // foremast
  for (let y = 11; y < h; y++) {                                                                                                // hull, raked bow
    const xa = 1 + Math.max(0, y - 11) + (y === 14 ? 1 : 0);
    for (let x = xa; x <= 50; x++) p.put(x, y, y === 11 ? '#3a4058' : y === 14 ? '#5a1c1e' : '#141a2a');
  }
  p.put(1, 11, '#6a7088');
  return p.done();
}
function drawShip(t) {
  if (t < SHIP.t0 || t >= SHIP.t1) return null;
  // it passes between Alcatraz and the city, then heads out under the bridge and melts into the fog
  const u = (t - SHIP.t0) / (SHIP.t1 - SHIP.t0), x = Math.round(lerp(SHIP.x0, SHIP.x1, u)), wl = Math.round(lerp(SHIP.wl0, SHIP.wl1, u)), y = wl - 14;
  const a = smooth(236, 344, x);
  if (a < 0.01) return null;
  for (let j = 0; j < 6; j++) {                                  // rippled reflection
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.2));
    ctx.globalAlpha = a * 0.26 * (1 - j / 6);
    ctx.drawImage(SHIP_C, 0, 13 - j, 52, 1, x + off, wl + 1 + j, 52, 1);
  }
  ctx.globalAlpha = a; ctx.drawImage(SHIP_C, x, y); ctx.globalAlpha = 1;
  const s = step(t, 1440);                                       // bow wave
  ctx.fillStyle = '#b8c4dc';
  for (let j = 0; j < 6; j++) if (hash(j, s, 83) > 0.4) { ctx.globalAlpha = a * 0.7 * (1 - j / 6); ctx.fillRect(x + 1 + j * 2, wl + (j & 1), 1, 1); }
  ctx.globalAlpha = 1;
  const blink = frac(t / 1.5) < 0.5;
  px(ctx, x + 8, y + 1, '#ffffff'); px(ctx, x + 42, y - 1, '#ffffff');
  halo(HL.mast, x + 8, y + 1, a * 0.8); halo(HL.mast, x + 42, y - 1, a * 0.7);
  px(ctx, x + 38, y + 9, '#ff3a30'); halo(HL.red, x + 38, y + 9, a * 0.7);
  halo(HL.shipDeck, x + 42, y + 5, a * 0.6);
  if (blink) halo(HL.red, x + 44, y - 1, a * 0.5);
  const e = hornEnv(t, SHIP_HORN);                               // a glow off the funnel while its horn sounds
  if (e > 0) { halo(HL.horn, x + 44, y, e * 0.6 * a); halo(HL.hornCore, x + 44, y, e * a); }
}

/* ================= Fog tongue pouring through the Gate, under and around the deck ================= */
const TG = { x: 136, y: 144, w: 312, h: 54, tile: 160 };
function renderTongueTex(seed) {
  const p = painter(TG.tile, TG.h);
  for (let x = 0; x < TG.tile; x++) {
    const a = TAU * x / TG.tile;
    const e = 6 + 2.6 * Math.sin(2 * a + seed) + 1.8 * Math.sin(5 * a + 1 + seed * 2) + 1.1 * Math.sin(11 * a + 2);
    for (let y = 0; y < TG.h; y++) {
      if (y < e - 0.5) continue;
      const wisp = 0.58 + 0.26 * Math.sin(y * 0.55 + 1.6 * Math.sin(2 * a + seed)) + 0.18 * Math.sin(y * 0.21 - 3 * a + seed * 2) + 0.12 * Math.sin(5 * a + y * 0.9);
      const d = smooth(e - 0.5, e + 4, y) * clamp(wisp, 0.2, 1) * (0.8 + 0.2 * (1 - y / TG.h));
      const q = Math.floor(clamp(d, 0, 1) * 4 + bayer(x, y)) / 4;
      if (q <= 0) continue;
      p.put(x, y, y < e + 1.5 ? '#b4c0dc' : y < e + 9 ? '#9aa8ca' : '#8492b6', Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
function renderTongueMask() {
  const p = painter(TG.w, TG.h);
  for (let x = 0; x < TG.w; x++) {
    const X = TG.x + x, la = smooth(TG.x, 196, X) * (1 - smooth(360, 446, X)), lowTop = 146 + Math.max(0, X - 352) * 0.3;
    for (let y = 0; y < TG.h; y++) {
      const Y = TG.y + y;
      const a = la * (1 - smooth(168, 195, Y)) * smooth(lowTop - 6, lowTop + 2, Y);
      const q = Math.floor(clamp(a, 0, 1) * 6 + bayer(x, y)) / 6;
      if (q > 0) p.put(x, y, '#ffffff', Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
function drawTongue(t) {
  const [sc, sx] = TG_SCR;
  sx.globalCompositeOperation = 'source-over';
  sx.clearRect(0, 0, TG.w, TG.h);
  const oa = scroll(t, 3, TG.tile), ob = scroll(t, 5, TG.tile);   // two layers flowing east at 2 and 3.3 px/s
  for (let k = -1; k * TG.tile < TG.w; k++) sx.drawImage(TONGUE_A, oa + k * TG.tile, 0);
  sx.globalAlpha = 0.75;
  for (let k = -1; k * TG.tile < TG.w; k++) sx.drawImage(TONGUE_B, ob + k * TG.tile, 7);
  sx.globalAlpha = 1;
  sx.globalCompositeOperation = 'destination-in';
  sx.drawImage(TONGUE_M, 0, 0);
  sx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.8;
  ctx.drawImage(sc, TG.x, TG.y + Math.round(1.6 * wave(t, 3, 0.2)));
  ctx.globalAlpha = 1;
}
const hornEnv = (t, tf) => t < tf - 0.1 || t > tf + 3.2 ? 0 : smooth(tf, tf + 0.2, t) * (1 - smooth(tf + 1.9, tf + 2.9, t));
function drawFoghorn(t) {
  for (const tf of FOGHORNS) {
    const e = hornEnv(t, tf);
    if (e <= 0) continue;
    halo(HL.horn, T0.cx + 4, 170, e);
    halo(HL.hornCore, T0.cx + 9, 167, e);
  }
  const blink = frac(t / 4) < 0.1;                               // the fog signal light on the south pier
  px(ctx, T0.cx + 9, 167, blink ? '#fff4d0' : '#8a7050');
  if (blink) halo(HL.hornCore, T0.cx + 9, 167, 0.5);
}

/* ================= Water details: moon glitter, light streaks, drifting ripples ================= */
const STREAKS = [
  { x: T0.cx, w: 6, col: '#ffa860', a: 0.24, seed: 1, y0: 186 }, { x: T1.cx, w: 5, col: '#ffa860', a: 0.18, seed: 2, y0: 184 },
  { x: MOON.x, w: 4, col: '#d8dcf2', a: 0.24, seed: 3, y0: 180 },
];
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, WATER_TOP);
}
function drawWaterDetails(t) {
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, WF);
  for (let y = 178, n = 0; y < WF - 1; y += 2, n++) for (let j = 0; j < 5; j++) {   // ripple troughs and glints drifting east
    const k = 2 + ((n * 7 + j * 5) % 7), len = 4 + Math.floor(hash(n, j, 41) * 12);
    const x = 176 + Math.round(frac(hash(n, j, 40) + k * t / LOOP) * (W - 136)) - 20;
    if (y < 180 && x > 336) continue;
    ctx.globalAlpha = j === 4 ? 0.2 : 0.36;
    rect(ctx, x, y + (j & 1), len, 1, j === 4 ? '#7a86aa' : '#070b18');
  }
  ctx.globalAlpha = 1;
}

/* ================= Alcatraz: rock, cellhouse, lighthouse, water tower ================= */
const ALC_TOP = [[0, 30], [2, 27], [6, 23], [10, 20], [14, 19], [18, 18], [44, 18], [48, 19], [52, 22], [56, 26], [59, 30]];
const ALC_WINS = [];
function renderAlcatraz() {
  const p = painter(60, 31);
  for (let x = 0; x < 60; x++) {
    const top = Math.round(ridgeAt(ALC_TOP, x));
    for (let y = top; y <= 30; y++) {
      let col = (hash(x >> 1, y >> 1, 91) < 0.3) ? '#142018' : bayer(x, y) < 0.5 ? '#1a1e2e' : '#20263a';
      if (y === top) col = x > 30 ? '#4a5270' : '#3a4260';
      if (y >= 29) col = '#0e1220';
      p.put(x, y, col);
    }
  }
  for (let y = 11; y <= 18; y++) for (let x = 16; x <= 40; x++) {                 // cellhouse
    let col = y === 11 ? '#3a3e50' : y === 12 ? '#8a90a8' : x === 16 ? '#9aa0b6' : '#5c6278';
    if ((y === 14 || y === 16) && x > 17 && x < 40 && (x & 1)) {
      col = '#1a1c28';
      if (hash(x, y, 92) < 0.18) ALC_WINS.push({ id: ALC_WINS.length, x: ALC.x + x, y: ALC.y + y, n: 3 + Math.floor(hash(x, y, 93) * 4), off: hash(x, y, 94) });
    }
    p.put(x, y, col);
  }
  for (let y = 6; y <= 12; y++) p.put(37, y, y === 6 ? '#6a6e82' : '#4a4e62');     // power-house chimney
  for (let y = 14; y <= 18; y++) for (let x = 5; x <= 10; x++) p.put(x, y, (x + y) % 3 ? '#2a2e3e' : '#1a1c28');   // warden's house ruin
  for (let y = 5; y <= 18; y++) { p.put(11, y, '#c8ccd8'); p.put(12, y, '#8a90a4'); }                                // lighthouse tower
  for (let x = 10; x <= 13; x++) p.put(x, 4, '#3a3e50');
  p.put(11, 3, '#fff4c0'); p.put(12, 3, '#fff4c0'); p.put(11, 2, '#fff8e0'); p.put(12, 2, '#fff8e0'); p.put(11, 1, '#2a2e3e'); p.put(12, 1, '#2a2e3e');
  for (let y = 8; y <= 11; y++) for (let x = 47; x <= 52; x++) p.put(x, y, y === 8 ? '#9aa0b6' : x === 47 ? '#8a90a4' : '#646a80'); // water tower
  for (let y = 12; y <= 18; y++) { p.put(47, y, '#3a3e50'); p.put(52, y, '#3a3e50'); if (y === 15) for (let x = 48; x < 52; x++) p.put(x, y, '#2e3244'); }
  for (let y = 22; y <= 27; y++) for (let x = 43; x <= 55; x++) {                 // dock buildings
    let col = y === 22 ? '#5a6078' : '#3a3e52';
    if (y === 24 && (x & 1) && x < 55) { col = '#1a1c28'; if (hash(x, 5, 95) < 0.35) ALC_WINS.push({ id: ALC_WINS.length, x: ALC.x + x, y: ALC.y + y, n: 2 + Math.floor(hash(x, 6, 95) * 3), off: hash(x, 7, 95) }); }
    p.put(x, y, col);
  }
  return p.done();
}
const BEAM_L = 118, BEAM_LV = 16;
function renderBeam(len, dir) {
  const w = Math.max(1, len), h = 13, oy = 6, p = painter(w, h);
  for (let d = 0; d < w; d++) {
    const th = 0.6 + d * 0.045, fall = Math.pow(1 - d / w, 1.1) * (d < 2 ? 0.6 : 1);
    for (let j = 0; j < h; j++) {
      const e = 1 - Math.abs(j - oy) / (th + 0.5);
      if (e <= 0) continue;
      const q = Math.floor(0.8 * fall * Math.min(1, e * 1.6) * 24 + bayer(d, j)) / 24;
      if (q > 0) p.put(dir > 0 ? d : w - 1 - d, j, '#fff0c8', Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
function drawAlcatraz(t) {
  for (let j = 0; j < 8; j++) {                                  // reflection in the bay
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.8) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.22 * (1 - j / 8);
    ctx.drawImage(ALC_C, 0, 28 - j * 2, 60, 1, ALC.x + off, ALC.y + 31 + j, 60, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(ALC_C, ALC.x, ALC.y);
  for (const w of ALC_WINS) if (hash(w.id + 40, Math.floor(frac(t / LOOP + w.off) * w.n), 6) < 0.6) px(ctx, w.x, w.y, '#ffd890');
  // the lighthouse turns 24 times per loop: long beams sideways, a flash when it swings toward us
  const th = TAU * 24 * t / LOOP, s = Math.sin(th), c = Math.cos(th);
  const lv = Math.round(Math.abs(s) * (BEAM_LV - 1)), dir = s >= 0 ? 1 : 0;
  if (lv > 0) {
    const b = BEAMS[dir][lv], a = 0.7 + 0.3 * Math.max(0, c);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
    ctx.drawImage(b, dir ? LH.x : LH.x - b.width + 1, LH.y - 6);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  halo(HL.lh, LH.x, LH.y, 0.7);
  if (c > 0.88) halo(HL.lhFlash, LH.x, LH.y, (c - 0.88) / 0.12);
  streak(ctx, t, LH.x, 3, '#fff0c0', 0.18 + 0.3 * Math.max(0, c - 0.6), 11, 202, WF);
}

/* ================= Sailboats with mast lights ================= */
function renderSailboat() {
  const p = painter(12, 13);
  for (let y = 0; y <= 10; y++) p.put(5, y, '#3a3e50');
  for (let y = 2; y <= 9; y++) { const wd = Math.round((y - 1) * 0.55); for (let x = 6; x <= 6 + wd; x++) p.put(x, y, x === 6 + wd ? '#8a94b0' : '#c4ccde'); }
  for (let y = 4; y <= 9; y++) { const wd = Math.round((y - 3) * 0.45); for (let x = 4 - wd; x <= 4; x++) p.put(x, y, '#a8b2c8'); }
  for (let x = 1; x <= 10; x++) { p.put(x, 10, '#e2e4ee'); p.put(x, 11, x > 1 && x < 10 ? '#262a3c' : '#e2e4ee'); }
  p.put(2, 12, '#141828'); for (let x = 3; x <= 9; x++) p.put(x, 12, '#141828');
  return p.done();
}
function renderMotorboat() {
  const p = painter(11, 7);
  for (let y = 0; y <= 3; y++) p.put(6, y, '#3a3e50');
  for (let x = 4; x <= 8; x++) { p.put(x, 3, '#aab0c4'); p.put(x, 4, x & 1 ? '#ffd890' : '#8a90a4'); }
  for (let x = 0; x <= 10; x++) p.put(x, 5, x === 0 ? '#8a90a4' : '#d8dae6');
  for (let x = 1; x <= 10; x++) p.put(x, 6, '#1a1e2e');
  return p.done();
}
const BOATS = [
  { spr: 0, y: 203, k: 1, ph: 0.62, dir: 1, mast: [5, 0], cabin: [6, 9] },
  { spr: 1, y: 193, k: 1, ph: 0.15, dir: -1, mast: [6, 0], cabin: [5, 4] },
];
function drawBoats(t, which) {
  for (const b of BOATS) {
    if ((b.spr === 1) !== which) continue;
    const spr = BOAT_C[b.spr], u = frac(b.k * t / LOOP + b.ph), span = W - 130 + 30;
    const x = Math.round(b.dir > 0 ? 130 + u * span : W + 30 - u * span);
    const y = b.y - spr.height + 1 + Math.round(0.7 * wave(t, 40, b.ph));
    if (x > W || x < 120) continue;
    for (let j = 0; j < 3; j++) { ctx.globalAlpha = 0.2 * (1 - j / 3); ctx.drawImage(spr, 0, spr.height - 2 - j, spr.width, 1, x, b.y + 1 + j, spr.width, 1); }
    ctx.globalAlpha = 1;
    ctx.drawImage(spr, x, y);
    px(ctx, x + b.mast[0], y + b.mast[1], '#ffffff'); halo(HL.mast, x + b.mast[0], y + b.mast[1], 0.75);
    halo(HL.cabin, x + b.cabin[0], y + b.cabin[1], 0.6);
    streak(ctx, t, x + b.mast[0], 2, '#fff4d8', 0.25, 13 + b.spr, b.y + 1, Math.min(WF, b.y + 12));
  }
}

/* ================= Waterfront: seawall, curved pier, the tall ship at Hyde Street Pier, sea lion floats ================= */
const PIER_LAMPS = [], SHIP_LIGHTS = [];
const pierPt = u => [206 + 92 * (1 - Math.cos(u * Math.PI / 2)), WF - 10 * Math.sin(u * Math.PI / 2)];
function renderWaterfront() {
  const [c, x] = makeCanvas(W, H), p = painter(W, H);
  for (let gx = 176; gx < W; gx++) { p.put(gx, WF, '#4a4a5c'); p.put(gx, WF + 1, '#1e1e2a'); p.put(gx, WF + 2, '#141420'); }
  for (let k = 0; k <= 140; k++) {                                 // the curved Municipal Pier
    const [px0, py0] = pierPt(k / 140);
    p.put(px0, py0, '#72728a'); p.put(px0, py0 + 1, '#26263a');
    if (k % 6 === 0) p.put(px0, py0 + 2, '#1a1a28');
  }
  for (let k = 1; k <= 12; k++) { const [lx, ly] = pierPt(k / 12.4); PIER_LAMPS.push([Math.round(lx), Math.round(ly) - 1]); p.put(lx, ly - 1, '#ffe2a0'); }
  for (let y = 205; y < WF; y++) for (let gx = 344; gx <= 351; gx++)                 // Hyde Street Pier, reaching out to the tall ship
    p.put(gx, y, y === 205 ? '#6a6a7e' : gx === 344 ? '#4e4e62' : gx === 351 ? '#22222e' : (y & 1) ? '#34344a' : '#3e3e50');
  for (const gx of [345, 350]) { p.put(gx, 204, '#1a1a24'); p.put(gx, 203, '#ffe2a0'); PIER_LAMPS.push([gx, 203]); }
  // the tall ship: black hull, white sheer line, gunports, three masts with yards
  for (let y = 206; y <= 210; y++) for (let gx = 352 + (y > 208 ? 1 : 0); gx <= 384 - (y > 207 ? y - 207 : 0); gx++)
    p.put(gx, y, y === 207 ? ((gx & 1) ? '#8a8e9e' : '#2a2c36') : y === 206 ? '#3a3c4a' : '#0e1016');
  const done = p.done();
  x.drawImage(done, 0, 0);
  line(x, 352, 205, 344, 201, '#2a2c38');                          // bowsprit
  const MASTS = [[358, 183], [367, 181], [376, 185]];
  x.globalAlpha = 0.45;
  line(x, 344, 201, 358, 183, '#3a3e52'); line(x, 358, 183, 367, 181, '#3a3e52'); line(x, 367, 181, 376, 185, '#3a3e52'); line(x, 376, 185, 384, 205, '#3a3e52');
  x.globalAlpha = 1;
  for (const [mx, my] of MASTS) {
    line(x, mx, my, mx, 205, '#5a5e74'); px(x, mx, my - 1, '#7a7e94');
    for (const [dy, hw] of [[4, 3], [8, 5], [13, 6], [18, 5]]) line(x, mx - hw, my + dy, mx + hw, my + dy, '#4a4e64');
  }
  const segs = [[344, 201, 358, 183], [358, 183, 367, 181], [367, 181, 376, 185], [376, 185, 384, 205]];
  for (const [ax, ay, bx, by] of segs) {                           // dressing lights along the stays
    const n = Math.max(2, Math.round(Math.hypot(bx - ax, by - ay) / 4));
    for (let k = 0; k < n; k++) SHIP_LIGHTS.push([Math.round(lerp(ax, bx, k / n)), Math.round(lerp(ay, by, k / n))]);
  }
  x.globalAlpha = 0.8;
  for (const [lx, ly] of SHIP_LIGHTS) px(x, lx, ly, '#d8a060');
  x.globalAlpha = 1;
  for (const [a, b, y] of [[427, 440, 208], [444, 460, 206], [462, 476, 209]]) { rect(x, a, y, b - a + 1, 1, '#5e5866'); rect(x, a, y + 1, b - a + 1, 1, '#1a1620'); }
  return c;
}
const SEA_LIONS = [[430, 208, 0], [435, 208, 1], [446, 206, 0], [451, 206, 1], [455, 206, 0], [464, 209, 1], [470, 209, 0]];
const LION_DIVE = { t: 131, i: 5 };
function drawSeaLions(t) {
  for (let i = 0; i < SEA_LIONS.length; i++) {
    const [sx, sy, f] = SEA_LIONS[i];
    if (i === LION_DIVE.i && t >= LION_DIVE.t - 0.6 && t < LION_DIVE.t + 40) {   // slides off, swims a while, hauls out again
      const dt = t - LION_DIVE.t;
      if (dt < 0) { rect(ctx, sx - 1, sy - 1, 4, 1, '#3a2a22'); continue; }
      if (dt < 1.2) {
        const s = dt / 1.2;
        ctx.fillStyle = '#c8d4ec';
        for (let k = 0; k < 5; k++) { ctx.globalAlpha = 1 - s; ctx.fillRect(Math.round(sx + 1 + (k - 2) * (1 + s * 2)), Math.round(sy + 1 - Math.sin(Math.PI * s) * (2 + (k & 1))), 1, 1); }
        ctx.globalAlpha = 1;
      } else if (dt < 38) {
        const hx = Math.round(sx + 4 + 10 * Math.sin((dt - 1.2) * 0.2)), hy = sy + 3;
        if (frac(dt / 5) < 0.7) { px(ctx, hx, hy, '#4a382c'); ctx.globalAlpha = 0.5; px(ctx, hx - 1, hy + 1, '#7a86aa'); px(ctx, hx + 1, hy + 1, '#7a86aa'); ctx.globalAlpha = 1; }
      }
      continue;
    }
    const up = hash(i, step(t, 160 + i * 16), 97) < 0.35;          // heads lift and bark now and then
    rect(ctx, sx, sy - 1, 4, 1, '#3e2e26'); rect(ctx, sx + (f ? 1 : 0), sy - 2, 3, 1, '#4a3a30'); px(ctx, sx + (f ? 2 : 1), sy - 2, '#8a7262');
    const hx = f ? sx : sx + 3;
    if (up) { px(ctx, hx, sy - 3, '#4a382c'); px(ctx, hx, sy - 4, '#5a4636'); } else px(ctx, hx, sy - 3, '#4a382c');
  }
}
function drawWaterfront(t) {
  ctx.drawImage(WATERFRONT_C.c, WATERFRONT_C.x, WATERFRONT_C.y);
  const s = step(t, 480);
  SHIP_LIGHTS.forEach(([lx, ly], i) => { if (hash(i, s, 98) < 0.25) px(ctx, lx, ly, '#fff4d0'); });
  halo(HL.shipGlow, 367, 196, 0.8);
  PIER_LAMPS.forEach(([lx, ly], i) => { halo(HL.pierLamp, lx, ly, 0.85 + 0.15 * wave(t, 700 + i * 13)); streak(ctx, t, lx, 1, '#ffd890', 0.3, 20 + i, ly + 3, Math.min(WF, ly + 11)); });
  drawSeaLions(t);
}

/* ================= The lower city between the hill and the water ================= */
const WARM = ['#ffd58a', '#f4bb6a', '#e89e56'], COOL = ['#d6e4ff', '#adc4ec'];
const CITY_WINS_A = [], CITY_WINS_B = [];
/* a painter that remembers which block owns each pixel, so windows hidden by a nearer block or a tree are dropped */
function cityPainter() {
  const p = painter(W, H), own = new Int16Array(W * H).fill(-1);
  let cur = -1, n = 0;
  const put = (i, y, col, a) => {
    p.put(i, y, col, a); i = Math.round(i); y = Math.round(y);
    if (i >= 0 && y >= 0 && i < W && y < H) own[y * W + i] = cur;
  };
  const block = () => (cur = n++);                // start a new owner (a block, a tree)
  const other = () => (cur = -1);
  const keep = wins => { let k = 0; for (const w of wins) if (w.b === undefined || own[w.y * W + w.x] === w.b) wins[k++] = w; wins.length = k; };
  return { ...p, put, block, other, keep };
}
const cityWin = (r, wins, x, y, b, lit) => wins.push({ id: wins.length, b, x, y, n: 2 + Math.floor(r() * 6), p: lit, off: r(),
  col: r() < 0.78 ? WARM[Math.floor(r() * 3)] : COOL[Math.floor(r() * 2)] });
function cityRow(p, r, xa, xb, base, hmin, hmax, pal, wins, o) {
  let x = xa;
  while (x < xb) {
    const acc = (o.accents || []).find(a => a.x >= x && a.x < x + o.wmax);
    if (acc) {                                     // leave room for a taller accent here and paint it last, in front
      if (acc.x - x >= 5) plainBlock(p, r, x, acc.x - x, base, base - hmin - Math.floor(r() * (hmax - hmin + 1)), pal[Math.floor(r() * pal.length)], wins, o);
      x = acc.x + acc.w;
      continue;
    }
    const w = o.wmin + Math.floor(r() * (o.wmax - o.wmin + 1));
    // mostly walk-ups of 2-5 floors, a low shop or garage now and then
    const low = r() < 0.18, h = low ? Math.max(5, hmin - 4 + Math.floor(r() * 4)) : hmin + Math.floor(r() * (hmax - hmin + 1));
    plainBlock(p, r, x, w, base, base - h, pal[Math.floor(r() * pal.length)], wins, o);
    x += w + (r() < 0.15 ? 1 : 0);
  }
  for (const a of o.accents || []) accentTower(p, r, a, base, wins, o);
  return x;
}
function plainBlock(p, r, x, w, base, top, col, wins, o) {
  const b = p.block(), bay = o.bays && w >= 9 && r() < 0.6, bx = x + 2 + Math.floor(r() * (w - 6));
  const wy = o.wy + (r() < 0.3 ? 1 : 0), wx = w >= 10 && r() < 0.3 ? 3 : 2;   // each block has its own window rhythm
  for (let y = top; y < base; y++) for (let i = x; i < x + w; i++) {
    let c = col;
    if (y === top) c = tint(col, '#9aa2c8', 0.3);
    else if (i === x + w - 1) c = tint(col, '#000000', 0.35);
    else if (bay && i >= bx && i < bx + 3 && y > top + 1) c = tint(col, '#9aa2c8', i === bx ? 0.16 : 0.08);
    p.put(i, y, c);
  }
  if (r() < 0.35 && w > 6) { const cx = x + 1 + Math.floor(r() * (w - 4)); for (let y = top - 2; y < top; y++) for (let i = cx; i < cx + 3; i++) p.put(i, y, y === top - 2 ? tint(col, '#9aa2c8', 0.3) : col); }
  for (let y = top + 2; y < base - 1; y += wy) for (let i = x + 1; i < x + w - 1; i += wx) {
    const v = r();
    if (v < o.lit) cityWin(r, wins, i, y, b, 0.6);
    else if (v < o.lit + 0.25) p.put(i, y, tint(col, '#000000', 0.45));
  }
}
/* a taller mid-century apartment block on the lower slope: pale concrete, balcony bands, a set-back top floor and a
   penthouse box, rising over Beach Street against the bay */
function accentTower(p, r, a, base, wins, o) {
  const b = p.block(), { x, w, h, col } = a, top = base - h;
  for (let y = top; y < base; y++) {
    const k = y - top, inset = k < 3 ? 1 : 0;
    for (let i = x + inset; i < x + w - inset; i++) {
      let c = col;
      if (k === 0 || k === 3) c = tint(col, '#9aa2c8', 0.34);                          // roof edge and the setback ledge
      else if (i === x + w - 1 - inset) c = tint(col, '#000000', 0.38);
      else if (i === x + inset) c = tint(col, '#9aa2c8', 0.14);
      else if (k > 3 && (k - 3) % 3 === 0) c = tint(col, '#9aa2c8', 0.2);             // balcony slabs
      p.put(i, y, c);
    }
  }
  const px0 = x + 2 + (a.pent || 0);
  for (let y = top - 3; y < top; y++) for (let i = px0; i < px0 + 4; i++) p.put(i, y, y === top - 3 ? tint(col, '#9aa2c8', 0.3) : i === px0 + 3 ? tint(col, '#000000', 0.3) : col);
  for (let y = top + 1; y < top + 3; y++) for (let i = x + 2; i < x + w - 2; i += 2) if (r() < 0.5) cityWin(r, wins, i, y, b, 0.7);   // the top floor
  for (let y = top + 5; y < base - 1; y += 3) for (let i = x + 2; i < x + w - 2; i += 2) {
    const v = r();
    if (v < o.lit + 0.08) cityWin(r, wins, i, y, b, 0.6);
    else if (v < o.lit + 0.3) p.put(i, y, tint(col, '#000000', 0.45));
  }
}
function cypress(p, cx, base, h, col) {             // a dark windswept Monterey cypress
  if (p.other) p.other();
  for (let y = base - h; y < base; y++) {
    const k = (y - base + h) / h, hw = Math.round((0.6 + 3.2 * Math.sin(Math.PI * Math.min(1, k * 1.1))) * (0.8 + 0.4 * hash(cx, y, 99)));
    for (let i = -hw; i <= hw + 1; i++) p.put(cx + i + Math.round(k * 2), y, i === -hw ? tint(col, '#6a7a9a', 0.25) : col);
  }
}
function renderCityBack() {
  const p = cityPainter(), r = rng(303);
  for (let y = WF + 3; y < H; y++) for (let x = 176; x < W; x++) p.put(x, y, bayer(x, y) < 0.3 ? '#161824' : '#11131c');   // streets and yards
  for (let y = WF + 3; y < 228; y++) for (let x = 176; x < 300; x++) {                  // Aquatic Park: sand and lawn
    p.put(x, y, y < WF + 6 ? ((x + y) & 1 ? '#3a3446' : '#2e2a3a') : bayer(x, y) < 0.5 ? '#0e1814' : '#121e18');
  }
  for (const [cx, h] of [[216, 10], [240, 8], [266, 11], [288, 9]]) cypress(p, cx, 227, h, '#0a120e');
  for (let y = 214; y < 228; y++) for (let x = 300; x <= 327; x++) {                   // the white streamline bathhouse
    const cut = (y - 214 < 3 && (x - 300 < 3 - (y - 214) || 327 - x < 3 - (y - 214)));
    if (cut) continue;
    p.put(x, y, y === 214 ? '#9aa0b4' : y === 221 ? '#4e5468' : x === 300 ? '#7e8498' : '#666c82');
  }
  for (let y = 210; y < 214; y++) for (let x = 306; x <= 321; x++) p.put(x, y, y === 210 ? '#a4aabd' : '#747a90');
  for (let x = 303; x < 326; x += 3) CITY_WINS_A.push({ id: 900 + x, x, y: 218, n: 1, p: 1, off: 0, col: '#ffd890' });
  cityRow(p, r, 176, 206, 228, 8, 12, ['#2e2a36', '#342a2a'], CITY_WINS_A, { wmin: 8, wmax: 14, wy: 3, lit: 0.3 });
  cityRow(p, r, 328, W, 228, 8, 15, ['#3a2626', '#2e2a36', '#342a2a', '#2a2e3a', '#402a24'], CITY_WINS_A, { wmin: 7, wmax: 18, wy: 3, lit: 0.32 });
  for (let x = 176; x < W; x++) { p.put(x, 228, '#1a1a24'); p.put(x, 229, (x >> 2) & 1 ? '#15151e' : '#1c1c26'); p.put(x, 230, '#15151e'); }   // Beach Street
  p.keep(CITY_WINS_A);
  return p.done();
}
// two taller apartment blocks break the roofline of the nearer row
const CITY_ACCENTS = [{ x: 388, w: 13, h: 43, col: '#34364a', pent: 1 }, { x: 444, w: 11, h: 34, col: '#323242', pent: 4 }];
function renderCityFront() {
  const p = cityPainter(), r = rng(404), PAL = ['#2c2e40', '#322c3c', '#28323c', '#34303a', '#2a2a36'];
  // low behind Aquatic Park (its lawn and cypresses stay in view), a wider mix of heights east of the bathhouse
  const x1 = cityRow(p, r, 212, 296, 249, 13, 20, PAL, CITY_WINS_B, { wmin: 9, wmax: 17, wy: 3, lit: 0.34, bays: true });
  cityRow(p, r, x1, W, 249, 10, 25, PAL, CITY_WINS_B, { wmin: 8, wmax: 17, wy: 3, lit: 0.34, bays: true, accents: CITY_ACCENTS });
  p.other();
  for (let x = 216; x < W; x += 13) { p.put(x, 247, '#ffe0a0'); p.put(x, 248, '#3a3440'); }
  for (const [cx, h] of [[322, 14], [398, 12], [452, 15]]) cypress(p, cx, 256, h, '#0a100e');
  cityRow(p, r, 296, W, 272, 11, 22, ['#1e2030', '#22202c', '#1c2430', '#24222e'], CITY_WINS_B, { wmin: 10, wmax: 20, wy: 4, lit: 0.26, bays: true });
  p.keep(CITY_WINS_B);
  return p.done();
}
const BEACH_CARS = [];
{ const r = rng(55); for (let i = 0; i < 6; i++) BEACH_CARS.push({ dir: i % 2 ? -1 : 1, n: 3 + Math.floor(r() * 4), ph: r() }); }
function drawCity(t) {
  ctx.drawImage(CITY_BACK.c, CITY_BACK.x, CITY_BACK.y);
  for (const w of CITY_WINS_A) if (w.p >= 1 || hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) px(ctx, w.x, w.y, w.col);
  for (const c of BEACH_CARS) {
    const u = frac(t * c.n / LOOP + c.ph), x = Math.round(c.dir > 0 ? 170 + u * 320 : 490 - u * 320);
    px(ctx, x, 229, '#0c0c12'); px(ctx, x + c.dir, 229, '#fff0c8'); px(ctx, x - c.dir, 229, '#ff3a2c');
    halo(HL.head, x + c.dir * 2, 229, 0.4);
  }
  ctx.drawImage(CITY_FRONT.c, CITY_FRONT.x, CITY_FRONT.y);
  for (const w of CITY_WINS_B) if (hash(w.id + 500, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) px(ctx, w.x, w.y, w.col);
  for (let x = 216; x < W; x += 13) halo(HL.streetLamp, x, 247, 0.8);
}

/* ================= Painted Victorians stepping down the hill =================
   Each house is painted twice at build time (all windows dark / all lit with curtains and lamps);
   per frame the dark row is drawn and each lit room is copied over from the lit version. */
const HOUSE_STYLES = [
  { style: 'queen', body: ['#1a1d38', '#252a4e', '#323964', '#414a7a', '#545e96'], trim: ['#9894b0', '#d8d4e4'], acc: '#8a3048' },
  { style: 'ital', body: ['#15231c', '#1f3127', '#2a4334', '#385744', '#4c6e56'], trim: ['#a8a894', '#e2e0cc'], acc: '#6a2a2a' },
  { style: 'stick', body: ['#2a1622', '#3c2030', '#502a40', '#663852', '#804a68'], trim: ['#b8a8a4', '#f0e2da'], acc: '#2a4a5a' },
  { style: 'ital2', body: ['#382c1c', '#503e26', '#685432', '#826a40', '#a08452'], trim: ['#c0b8a4', '#f6f0e0'], acc: '#2e4a3e' },
];
const ROOM_KINDS = [
  { bay1: 'warm', bay2: 'toggle', side: 'toggle', top: 'toggle', gable: 'toggle' },
  { bay1: 'person', bay2: 'toggle', side: 'tv', top: 'toggle' },
  { bay1: 'cat', bay2: 'warm', side: 'toggle', top: 'toggle', gable: 'toggle' },
  { bay1: 'toggle', bay2: 'warm', side: 'toggle', top: 'toggle' },
];
const houseG = i => sw(i * HOUSE_W) - 8;           // parlour-floor level (the garage sits below it)
const ROOMS = [], PORCH = [];
function paintHouse(p, i, L) {
  const hs = HOUSE_STYLES[i], x0 = i * HOUSE_W, x1 = x0 + HOUSE_W - 1, g = houseG(i), ft = g - 66, B = hs.body, T = hs.trim;
  const put = (x, y, col) => p.put(x, y, i === 3 ? lit(x, y, col) : col);            // the last house catches the streetlamp
  const room = (key, rects) => { if (!L) ROOMS.push({ id: ROOMS.length, house: i, key, kind: ROOM_KINDS[i][key], rects, n: 3 + Math.floor(hash(i, ROOMS.length, 61) * 4), off: hash(i, ROOMS.length, 62) }); };
  const glass = (gx, gy, gw, gh, dim, curtains) => {
    for (let y = gy; y < gy + gh; y++) for (let x = gx; x < gx + gw; x++) {
      let col;
      if (L) {
        const v = (y - gy) / gh;
        col = v < 0.3 ? '#ffe6ac' : v < 0.72 ? '#f8c478' : '#e2a05a';
        if (curtains && (x - gx < 2 || gx + gw - 1 - x < 2)) col = tint(hs.acc, '#ffb060', 0.35 + (x & 1) * 0.1);
        if (dim) col = tint(col, '#40241c', 0.4);
      } else col = (x - gx + y - gy) % 7 === 0 && y < gy + gh - 2 ? '#262c46' : '#12162a';
      put(x, y, col);
    }
  };
  // garage level: stucco, a panelled garage door, steep front stairs
  for (let x = x0; x <= x1; x++) for (let y = g; y < sw(x); y++) put(x, y, bayer(x, y) < 0.5 ? '#22222c' : '#262632');
  for (let x = x0 + 5; x <= x0 + 21; x++) for (let y = g + 2; y < sw(x); y++)
    put(x, y, x === x0 + 5 || x === x0 + 21 || y === g + 2 ? T[0] : (y - g) % 3 === 1 ? '#201c26' : '#322e3a');
  for (let x = x0 + 26; x <= x0 + 37; x++) for (let y = g; y < sw(x); y++)
    put(x, y, x === x0 + 26 || x === x0 + 37 ? '#18161e' : (y - g) % 2 === 0 ? tint(T[0], '#000000', 0.25) : '#2a2632');
  // clapboard facade with corner boards and belt courses
  for (let y = ft; y < g; y++) for (let x = x0; x <= x1; x++) {
    let col = (y - ft) % 3 === 2 ? B[1] : bayer(x, y) < 0.2 + 0.25 * (1 - (y - ft) / 66) ? B[3] : B[2];
    if (x === x0) col = T[1]; else if (x === x1) col = T[0]; else if (x === x1 - 1) col = B[0];
    put(x, y, col);
  }
  for (const by of [g - 22, g - 44]) for (let x = x0; x <= x1; x++) { put(x, by, T[1]); put(x, by + 1, B[0]); }
  // two-storey bay window
  for (const [fy, key] of [[g - 22, 'bay1'], [g - 44, 'bay2']]) {
    for (let x = x0 + 3; x <= x0 + 23; x++) { put(x, fy + 2, T[1]); put(x, fy + 3, T[0]); put(x, fy + 18, T[0]); put(x, fy + 19, B[1]); put(x, fy + 20, B[0]); }
    for (let y = fy + 2; y <= fy + 21; y++) put(x0 + 24, y, B[0]);
    glass(x0 + 4, fy + 4, 3, 14, true, false); glass(x0 + 8, fy + 4, 11, 14, false, true); glass(x0 + 20, fy + 4, 3, 14, true, false);
    for (let y = fy + 4; y < fy + 18; y++) { put(x0 + 7, y, T[1]); put(x0 + 19, y, T[1]); put(x0 + 3, y, T[0]); put(x0 + 23, y, T[0]); }
    for (let x = x0 + 8; x <= x0 + 18; x++) put(x, fy + 9, T[1]);
    room(key, [[x0 + 4, fy + 4, 19, 14]]);
  }
  // front door with a lit transom, a hood and a porch lantern
  for (let x = x0 + 27; x <= x0 + 36; x++) { put(x, g - 21, T[1]); put(x, g - 20, T[0]); }
  for (let y = g - 19; y < g; y++) for (let x = x0 + 28; x <= x0 + 35; x++) {
    let col = x === x0 + 28 || x === x0 + 35 ? T[1] : hs.acc;
    if (y <= g - 17 && col !== T[1]) col = y === g - 19 ? '#ffe6ac' : '#f4b870';                       // lit transom
    else if (col === hs.acc && ((y - g + 16) % 7 === 0 || x === x0 + 29 || x === x0 + 34)) col = tint(hs.acc, '#000000', 0.35);
    else if (col === hs.acc && x === x0 + 30) col = tint(hs.acc, '#ffffff', 0.15);
    put(x, y, col);
  }
  put(x0 + 37, g - 19, '#18161e'); put(x0 + 37, g - 18, '#fff0c0'); put(x0 + 37, g - 17, '#ffc870');
  if (!L) PORCH.push([x0 + 37, g - 18]);
  // first-floor window over the door
  for (let y = g - 41; y <= g - 26; y++) for (let x = x0 + 28; x <= x0 + 35; x++) put(x, y, T[1]);
  glass(x0 + 29, g - 40, 6, 13, false, false);
  for (let x = x0 + 29; x <= x0 + 34; x++) put(x, g - 34, T[1]);
  room('side', [[x0 + 29, g - 40, 6, 13]]);
  // top floor and roof, by style
  const topWins = xs => {
    for (const wx of xs) {
      for (let x = wx - 1; x <= wx + 6; x++) { put(x, ft + 7, T[1]); put(x, ft + 19, T[1]); }
      for (let y = ft + 7; y <= ft + 19; y++) { put(wx - 1, y, T[1]); put(wx + 6, y, T[1]); }
      glass(wx, ft + 8, 6, 11, false, false);
    }
    room('top', xs.map(wx => [wx, ft + 8, 6, 11]));
  };
  const cornice = (ext) => {
    for (let x = x0 - ext; x <= x1 + ext; x++) { put(x, ft - 3, T[1]); put(x, ft - 2, T[0]); put(x, ft - 1, B[0]); }
    for (let x = x0; x <= x1; x++) {
      const br = (x - x0) % 5 === 1;
      put(x, ft, br ? T[1] : B[0]); put(x, ft + 1, br ? T[0] : B[1]); put(x, ft + 2, x & 1 ? T[1] : B[0]); put(x, ft + 3, T[0]);
    }
  };
  if (hs.style === 'queen') {
    topWins([x0 + 5, x0 + 14]);
    for (let y = ft - 13; y < ft; y++) {                                   // fish-scale gable over the bay
      const hw = y - (ft - 13);
      for (let x = x0 + 12 - hw; x <= x0 + 13 + hw; x++) {
        const edge = x <= x0 + 13 - hw || x >= x0 + 12 + hw;
        put(x, y, edge ? T[1] : ((x + ((y >> 1) & 1) * 2) % 4 === 0 && (y & 1)) ? tint(hs.acc, '#000000', 0.3) : B[2]);
      }
    }
    for (let x = x0 - 1; x <= x0 + 27; x++) { put(x, ft, T[0]); put(x, ft + 1, B[0]); }
    put(x0 + 12, ft - 15, T[1]); put(x0 + 13, ft - 15, T[1]); put(x0 + 12, ft - 14, T[0]); put(x0 + 13, ft - 14, T[0]);
    glass(x0 + 11, ft - 7, 4, 4, false, false);
    room('gable', [[x0 + 11, ft - 7, 4, 4]]);
    for (let y = g - 44; y >= ft - 2; y--) for (let dx = 0; dx <= 16; dx++) {            // the round corner turret
      const u = dx / 16, sh = u < 0.12 ? 3 : u < 0.55 ? 2 : u < 0.85 ? 1 : 0;
      put(x0 + 26 + dx, y, y === ft - 2 || y === g - 44 ? T[1] : (y - ft) % 3 === 2 ? B[Math.max(0, sh - 1)] : B[sh]);
    }
    const tw = [[x0 + 28, 2, true], [x0 + 32, 5, false], [x0 + 40, 2, true]];
    for (const [wx, ww, dim] of tw) glass(wx, ft + 4, ww, 13, dim, false);
    room('top2', tw.map(([wx, ww]) => [wx, ft + 4, ww, 13]));
    ROOMS[ROOMS.length - 1].kind = 'warm';
    for (let y = ft - 24; y < ft - 2; y++) {                               // witch's-hat roof
      const hw = Math.round((y - (ft - 24)) * 9 / 22);
      for (let x = x0 + 34 - hw; x <= x0 + 34 + hw; x++) put(x, y, x === x0 + 34 - hw ? T[0] : bayer(x, y) < 0.5 ? '#1c1a2c' : '#262238');
    }
    for (let y = ft - 28; y < ft - 24; y++) put(x0 + 34, y, T[1]);
  } else if (hs.style === 'stick') {
    topWins([x0 + 5, x0 + 14, x0 + 29]);
    for (let y = ft - 18; y < ft; y++) {                                   // steep gable with stickwork
      const hw = Math.round((y - (ft - 18)) * 23 / 18);
      for (let x = x0 + 22 - hw; x <= x0 + 21 + hw; x++) {
        const edge = x <= x0 + 23 - hw || x >= x0 + 20 + hw;
        put(x, y, edge ? (x <= x0 + 22 - hw || x >= x0 + 21 + hw ? T[1] : T[0]) : (x - x0) % 4 === 2 || y === ft - 6 ? T[0] : B[2]);
      }
    }
    for (let x = x0 - 2; x <= x1 + 2; x++) { put(x, ft, T[1]); put(x, ft + 1, T[0]); put(x, ft + 2, B[0]); }
    glass(x0 + 19, ft - 12, 6, 5, false, false);
    room('gable', [[x0 + 19, ft - 12, 6, 5]]);
  } else {
    topWins([x0 + 5, x0 + 14, x0 + 29]);
    cornice(1);
    if (hs.style === 'ital2') for (let y = ft - 9; y < ft - 3; y++) {      // pediment over the bay
      const hw = Math.round((y - (ft - 9)) * 12 / 6);
      for (let x = x0 + 13 - hw; x <= x0 + 13 + hw; x++) put(x, y, x === x0 + 13 - hw || x === x0 + 13 + hw || y === ft - 4 ? T[1] : B[2]);
    }
    else for (const fx of [x0 + 1, x0 + 22, x0 + 42]) { put(fx, ft - 4, T[1]); put(fx, ft - 5, T[0]); }
    if (i === 1) for (let y = ft - 12; y < ft - 3; y++) for (let x = x0 + 34; x <= x0 + 38; x++)   // chimney
      put(x, y, y === ft - 12 ? '#6a3e34' : x === x0 + 38 ? '#2a1816' : (y + (x >> 1)) % 3 === 0 ? '#3a221e' : '#4a2c28');
  }
  if (i === 3) for (let y = ft - 3; y < sw(x1 + 3); y++) for (let x = x1 + 1; x <= x1 + 2; x++) put(x, y, x === x1 + 1 ? '#15131c' : '#1c1a24');   // exposed side wall
}
const CHIMNEY = { x: HOUSE_W + 36, y: houseG(1) - 66 - 13 };
function renderHouses(L) {
  const p = painter(W, H);
  for (let i = 0; i < 4; i++) paintHouse(p, i, L);
  return p.done();
}

/* ================= The billboard: a lamplit sign on a steel scaffold on the last Victorian's flat roof =================
   A classic rooftop board: the face (BILL) in a steel frame, a painted apron and a grated catwalk under it, three
   small lamps on short outriggers aimed up at the face, uprights with X braces and a ladder dropping behind the
   cornice. Its own ad is an invented wharf crab shack's poster, FRESH CRAB, with a cut-out crab whose claws snap,
   lit like the night around it (BILL_NIGHT). */
const BILL_LAMPS = [BILL.x + 7, BILL.x + 24, BILL.x + 40], BILL_LAMP_Y = BILL.y + BILL.h;   // lamp lenses (aimed up)
const STEEL = { d: '#111018', m: '#1b1a24', h: '#34343f', l: '#4e4c5e', w: '#9a7450' };
function renderBillboard() {
  const [c, x] = makeCanvas(W, H);
  const L = BILL.x - 2, R = BILL.x + BILL.w + 1, T = BILL.y - 2, B = BILL.y + BILL.h + 1;   // frame box
  const CW = B + 1, AP = CW + 2, ROOF = 142;                     // catwalk deck, apron; the cornice hides everything from 142 down
  const posts = [L + 3, L + 18, L + 33, R - 4], LAD = posts[1] + 5, P0 = AP + 3;
  for (let k = 0; k + 1 < posts.length; k++) {                   // X braces between the uprights (not across the ladder)
    const a = posts[k] + 1, b = posts[k + 1] - 1;
    if (k === 1) continue;
    line(x, a, P0, b, ROOF, STEEL.m); line(x, a, ROOF, b, P0, STEEL.m);
  }
  rect(x, L, P0 + 6, R - L + 1, 1, STEEL.m);                    // mid-height tie
  posts.forEach(p => { rect(x, p, P0, 1, ROOF - P0, STEEL.d); rect(x, p - 1, P0, 1, ROOF - P0, STEEL.h); });
  for (let y = CW + 2; y < ROOF; y++) {                          // the service ladder up to the catwalk
    px(x, LAD, y, STEEL.h); px(x, LAD + 3, y, STEEL.d);
    if (y % 2 === 0) rect(x, LAD + 1, y, 2, 1, STEEL.m);
  }
  // apron hung under the catwalk: painted green, a blank maker's plate in the middle
  rect(x, L + 1, AP, R - L - 1, 3, '#1c3a30'); rect(x, L + 1, AP, R - L - 1, 1, '#2e5646'); rect(x, L + 1, AP + 2, R - L - 1, 1, '#142a24');
  rect(x, BILL.x + (BILL.w >> 1) - 4, AP + 1, 8, 1, '#8a8a7a');
  // catwalk: grated deck with end brackets and railing stanchions
  rect(x, L - 2, CW, R - L + 5, 1, STEEL.l); rect(x, L - 2, CW + 1, R - L + 5, 1, STEEL.d);
  for (let i = L - 1; i <= R + 2; i += 2) px(x, i, CW, STEEL.h);
  line(x, L - 2, CW + 2, L + 1, CW + 5, STEEL.d); line(x, R + 2, CW + 2, R - 1, CW + 5, STEEL.d);
  for (const sx of [L - 2, R + 2]) rect(x, sx, CW - 5, 1, 5, STEEL.m);
  rect(x, L - 2, CW - 5, 2, 1, STEEL.h); rect(x, R + 1, CW - 5, 2, 1, STEEL.h);
  // frame: dark steel with a lit inner bead
  rect(x, L, T, R - L + 1, B - T + 1, STEEL.m);
  rect(x, L, T, R - L + 1, 1, STEEL.h); rect(x, L, B, R - L + 1, 1, STEEL.d);
  rect(x, L + 1, T + 1, R - L - 1, 1, '#6a6474'); rect(x, L + 1, T + 1, 1, B - T - 1, '#5a5666');
  rect(x, R - 1, T + 1, 1, B - T - 1, '#2e2c38'); rect(x, L + 1, B - 1, R - L - 1, 1, STEEL.w);   // bottom bead catches the lamps
  rect(x, BILL.x, BILL.y, BILL.w, BILL.h, '#0d0f24');            // (the face itself is painted per frame)
  // floodlamps on short outriggers at the catwalk's front edge, lenses tipped up at the face
  for (const lx of BILL_LAMPS) {
    rect(x, lx - 1, BILL_LAMP_Y + 1, 3, 1, STEEL.h); px(x, lx - 1, BILL_LAMP_Y + 1, STEEL.l); px(x, lx + 1, BILL_LAMP_Y + 1, STEEL.m);
    rect(x, lx - 1, BILL_LAMP_Y, 3, 1, '#d8b074'); px(x, lx, BILL_LAMP_Y, '#f0d49c');
  }
  return c;
}
/* the city's own ad: FRESH / CRAB and a red crab on cream poster paper, lit in fans from the lamps below */
const PAPER = '#f2dba6', INK = '#1f2748', RED = '#c8262c', RED_D = '#8a1c20', RED_H = '#ec5a4a';
// the poster at night (gradeAd): pasted paper the fog has got at, under three small lamps and cooled towards the sky
// behind it, so the bridge's lit towers and deck stay brighter than the sign beside them. The lenses, their halos and
// the light the drizzle catches are stepped down to match
const BILL_NIGHT = { level: 0.74, tint: '#15244c', mix: 0.1, wear: 0.3, seed: 73 };
const CRAB_COL = { R: '#d4452c', H: '#ff8a5a', D: '#8e2418', e: INK, s: '#8e2418' };
const CRAB_O = { x: 1, y: 7 };                      // crab body origin on the face; the pincers sit above its arms
const CRAB_BODY = [
  '..D...........D..', '..RD..e...e..DR..', '...RD.s...s.DR...', '....RHHHHHHHR....', '..RRHRRRRRRRHRR..',
  '.RRRRRRRRRRRRRRR.', 'RRRRRRRRRRRRRRRRR', 'DRRRRRRRRRRRRRRRD', '.DDRRRRRRRRRRRDD.', 'D..DDDDDDDDDDD..D', '.D.D.........D.D.'];
const PINCER = [['RR.R.', 'RRRR.', 'RHRRD', '.RRD.'], ['R...R', 'RR.RR', 'RHRRD', '.RRD.']];   // closed, open (left claw)
const BIG = {
  C: ['.11111', '111111', '11....', '11....', '11....', '11....', '111111', '.11111'],
  R: ['11111.', '111111', '11..11', '111111', '11111.', '11.11.', '11..11', '11..11'],
  A: ['.1111.', '111111', '11..11', '11..11', '111111', '111111', '11..11', '11..11'],
  B: ['11111.', '111111', '11..11', '11111.', '111111', '11..11', '111111', '11111.'],
};
function billLight(i, j, dim) {                     // 0..1: fans of light from the lamps under the face
  let li = 0;
  BILL_LAMPS.forEach((lx, k) => {
    const d = BILL.h + 1 - j, dx = (i - (lx - BILL.x)) / (3.5 + d * 1.0);
    li = Math.max(li, (k === 0 && dim ? 0.08 : 1) * Math.exp(-dx * dx) * (1 - d / 30));
  });
  return li;
}
function renderPoster(dim, ls, rs) {                // ls/rs: claw states (bit 0 open, bit 1 raised)
  const { w, h } = BILL, [c, x] = makeCanvas(w, h);
  rect(x, 0, 0, w, h, PAPER);
  text(x, 'FRESH', 23, 2, INK);
  let bx = 19;
  for (const ch of 'CRAB') {                        // chunky poster lettering: a lit top edge, a shaded bottom
    BIG[ch].forEach((row, j) => { for (let i = 0; i < 6; i++) if (row[i] === '1') px(x, bx + i, 9 + j, j === 0 ? RED_H : j === 7 ? RED_D : RED); });
    bx += 7;
  }
  CRAB_BODY.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') px(x, CRAB_O.x + i, CRAB_O.y + j, CRAB_COL[row[i]]); });
  [ls, rs].forEach((s, side) => {                   // pincers over the arms; a raised claw lifts 1 px on a longer arm
    const g = PINCER[s & 1], up = s & 2 ? 1 : 0, x0 = side ? CRAB_O.x + 12 : CRAB_O.x, y0 = CRAB_O.y - 4 - up;
    g.forEach((row, j) => { for (let i = 0; i < 5; i++) {
      const ch = row[side ? 4 - i : i];
      if (ch !== '.') px(x, x0 + i, y0 + j, CRAB_COL[ch]);
    } });
    if (up) px(x, side ? CRAB_O.x + 14 : CRAB_O.x + 2, CRAB_O.y - 1, CRAB_COL.D);
  });
  // light it: a cool shade where the lamps don't reach, warm and full in their fans
  const img = x.getImageData(0, 0, w, h), d = img.data;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const lv = Math.floor((0.3 + 0.7 * billLight(i, j, dim)) * 4 + bayer(i, j)) / 4;
    let f = 0.7 + 0.3 * Math.min(1, lv);
    if (dim) f *= 1 - 0.16 * Math.exp(-(((i - (BILL_LAMPS[0] - BILL.x)) / 12) ** 2));
    const o = (j * w + i) * 4;
    d[o] = Math.round(d[o] * Math.pow(f, 1.1)); d[o + 1] = Math.round(d[o + 1] * Math.pow(f, 1.22)); d[o + 2] = Math.round(d[o + 2] * Math.pow(f, 0.9));
  }
  x.putImageData(img, 0, 0);
  return gradeAd(c, BILL_NIGHT);
}
const BILL_FLICK = [[71.2, 73.6], [203.4, 204.5]];           // the left floodlamp sputters
function billLampI(t) {
  for (const [a, b] of BILL_FLICK) if (t >= a && t < b) { const v = hash(23, Math.floor(t * 24), 5); return v > 0.55 ? 1 : v > 0.3 ? 0.45 : 0.1; }
  return 1;
}
function clawState(t, side) {                       // two quick snaps every 4 s, and a slow wave lifting each claw in turn
  const k = (step(t, 960) + side) % 16;
  return (k === 0 || k === 2 ? 1 : 0) | (wave(t, 24, side * 0.5) > 0.55 ? 2 : 0);
}
function drawBillboard(t, pitch) {
  const lv = billLampI(t);
  halo(HL.billGlow, BILL.x + (BILL.w >> 1), BILL.y + BILL.h - 4, (0.9 + 0.1 * wave(t, 6, 0.3)) * (2 + lv) / 3);
  blit(BILL_C);
  if (!PITCH.covers(t, pitch)) {
    const s = clawState(t, 0) * 4 + clawState(t, 1);
    ctx.drawImage(POSTERS[0][s], BILL.x, BILL.y);
    if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(POSTERS[1][s], BILL.x, BILL.y); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 0 ? lv : 1;
    if (a < 1) rect(ctx, lx - 1, BILL_LAMP_Y, 3, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.billLamp, lx, BILL_LAMP_Y, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= The street: sidewalks, the two cable-car tracks, a low wall over the drop ================= */
const STREET_POOLS = [[LAMP.x, sw(LAMP.x) + 14, 80, 22, '#ffb466', 0.72], [LAMP.x, sw(LAMP.x) + 1, 46, 8, '#ffb466', 0.55],
  [LAMP.x + 6, sw(LAMP.x) - 30, 46, 40, '#ffb466', 0.45]];
function renderStreet() {
  const p = painter(W, H);
  const put = (x, y, col) => p.put(x, y, lit(x, y, col, STREET_POOLS));
  for (let x = 0; x < 340; x++) {
    const s = sw(x);
    for (let y = s; y < H; y++) {
      const k = y - s;
      let col;
      if (k <= 2) col = k === 0 ? '#4c4a5a' : (x + 3) % 12 === 0 ? '#2a2834' : '#3a3846';            // far sidewalk
      else if (k === 3) col = '#262532'; else if (k === 4) col = '#141320';                           // curb face, gutter
      else if (k < 30) {                                                                               // wet asphalt, two tracks
        col = hash(x >> 1, y, 13) < 0.28 ? '#1e2030' : '#1a1c29';
        if (k === 9 || k === 12 || k === 19 || k === 22) col = x % 6 === 0 ? '#80869e' : '#565c74';     // rails
        else if (k === 10 || k === 20) col = '#0b0c14';                                                // cable slots
        else if (k === 13 || k === 23) col = '#15161f';
        else if (hash(x >> 2, y, 14) < 0.05) col = '#2a2e42';                                           // moonlit wet sheen
      } else if (k === 30) col = '#56546a'; else if (k === 31) col = '#383646';                       // near curb
      else col = (k - 32) % 9 === 8 || (x + (k >> 1)) % 18 === 0 ? '#20202a' : bayer(x, y) < 0.15 ? '#32323f' : '#2a2a36';
      put(x, y, col);
    }
  }
  for (let x = ROW_END + 3; x < 340; x++) {                                                            // stone wall + iron railing
    const s = sw(x);
    for (let y = s - 6; y < s; y++) put(x, y, y === s - 6 ? '#5e5a6e' : ((x + ((y >> 2) & 1) * 4) % 8 === 0 || (s - y) % 4 === 0) ? '#23212c' : '#3a3644');
    put(x, s - 13, '#3a3a48'); put(x, s - 12, '#15151c'); put(x, s - 7, '#15151c');
    if (x % 3 === 0) for (let y = s - 11; y < s - 7; y++) put(x, y, '#15151c');
  }
  // globe streetlamp on the far sidewalk
  const LX = LAMP.x, base = sw(LX);
  for (let y = base - 4; y < base; y++) for (let x = LX - 2; x <= LX + 3; x++) put(x, y, x === LX - 2 || y === base - 4 ? '#3a3a4a' : '#15141c');
  for (let y = 191; y < base - 4; y++) { put(LX, y, '#3a3a4a'); put(LX + 1, y, '#141319'); }
  for (const cy of [212, 222]) for (let x = LX - 1; x <= LX + 2; x++) put(x, cy, x === LX - 1 ? '#4a4a5a' : '#15141c');
  for (let x = LX - 1; x <= LX + 2; x++) { put(x, 190, '#15141c'); put(x, 189, '#2a2a36'); }
  const GLOBE = [[182, 1, 2, '#15141c'], [183, 0, 3, '#15141c'], [184, -1, 4, 'g'], [185, -2, 5, 'g'], [186, -2, 5, 'g'], [187, -2, 5, 'g'], [188, -1, 4, 'g']];
  for (const [y, a, b, c] of GLOBE) for (let x = LX + a; x <= LX + b; x++) {
    const e = x === LX + a || x === LX + b;
    p.put(x, y, c !== 'g' ? c : e ? '#ffc070' : (y < 187 && x >= LX && x <= LX + 1) ? '#ffffff' : '#fff0c0');
  }
  p.put(LX, 181, '#15141c'); p.put(LX + 1, 181, '#15141c');
  return p.done();
}
function renderCone() {                            // soft dithered light falling from the globe onto the street
  const w = 110, h = 74, p = painter(w, h), cx = 55;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const hw = 3 + j * 0.62, d = Math.abs(i - cx) / hw;
    if (d >= 1) continue;
    const a = 0.15 * Math.pow(1 - j / h, 0.7) * (1 - d * d);
    const q = Math.floor(a * 40 + bayer(i, j)) / 40;
    if (q > 0) p.put(i, j, '#ffc47a', Math.round(q * 255));
  }
  return p.done();
}
const LAMP_FLICK = [[97, 98.4], [188.6, 190]];
function lampI(t) {
  let v = 0.95 + 0.05 * wave(t, 1500, 0.3);
  for (const [a, b] of LAMP_FLICK) if (t >= a && t < b) { const hv = hash(11, Math.floor(t * 20)); v *= hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.15; }
  return v;
}

/* ================= Cable cars: maroon and cream, sheared onto the 1:4 grade ================= */
const CAR_W = 46, CAR_H = 23;
function renderCarBase(seed) {
  const p = painter(CAR_W, CAR_H), r = rng(seed);
  const MAR = '#6e1a26', MARH = '#9a323c', MARD = '#420e16', CREAM = '#e2d4ae', CREAMD = '#a8987a', GOLD = '#d4a444',
    ROOF = '#2a2a34', ROOFH = '#4c4c5c', DARK = '#17121a', W1 = '#ffdc96', W2 = '#f0b464', BRASS = '#c8a050', SIL = '#2e2228', SKIN = '#d8a07a';
  for (let x = 1; x < CAR_W - 1; x++) { p.put(x, 2, ROOFH); p.put(x, 3, ROOF); }
  for (let x = 12; x < CAR_W - 10; x++) p.put(x, 1, (x - 12) % 4 === 1 ? '#ffd890' : '#34343e');        // clerestory with lit lights
  for (const [a, b] of [[2, 10], [35, 43]]) for (let x = a; x <= b; x++) { p.put(x, 0, CREAMD); p.put(x, 1, (x - a) % 2 ? '#5a4a3a' : CREAM); }
  p.put(13, 0, GOLD); p.put(13, 1, '#8a6a2a');                                                            // the bell
  for (let x = 1; x < CAR_W - 1; x++) { p.put(x, 4, CREAM); p.put(x, 5, GOLD); }
  for (let y = 6; y <= 12; y++) for (let x = 1; x <= 44; x++) {
    let col = DARK;
    if (x >= 16 && x <= 36) {                                                                              // closed cabin
      const post = (x - 16) % 5 === 0;
      col = post ? CREAM : y < 9 ? W1 : W2;
    }
    if (x === 1 || x === 8 || x === 15 || x === 44) col = BRASS;
    p.put(x, y, col);
  }
  for (const hx of [18, 24, 28, 33]) if (r() < 0.8) {                                                  // passengers backlit in the cabin
    const hy = 8 + Math.floor(r() * 2);
    for (let y = hy; y <= hy + 1; y++) { p.put(hx, y, '#6a4030'); p.put(hx + 1, y, '#6a4030'); }
    for (let y = hy + 2; y <= 12; y++) for (let x = hx - 1; x <= hx + 2; x++) p.put(x, y, '#5a3a30');
  }
  for (const sx of [3, 6, 10]) { p.put(sx, 8, SKIN); p.put(sx, 7, '#3a2418'); for (let y = 9; y <= 12; y++) p.put(sx, y, ['#3a4a6a', '#5a2a2a', '#2e3a2e'][sx % 3]); }   // riders on the outward benches
  p.put(13, 6, '#2a2430'); p.put(13, 7, SKIN); for (let y = 8; y <= 12; y++) p.put(13, y, '#1e2a44'); p.put(12, 9, '#1e2a44'); // gripman at the lever
  for (const sx of [39, 41]) { p.put(sx, 7, SKIN); for (let y = 8; y <= 12; y++) p.put(sx, y, '#3a3040'); }
  for (let x = 1; x < CAR_W - 1; x++) p.put(x, 13, GOLD);
  for (let y = 14; y <= 18; y++) for (let x = 1; x < CAR_W - 1; x++) p.put(x, y, y === 14 ? MARH : MAR);
  for (const [a, b] of [[3, 13], [17, 35], [38, 43]]) for (let x = a; x <= b; x++) p.put(x, 16, '#b88a3a');   // gold pinstripe
  p.put(25, 16, CREAM); p.put(27, 16, CREAM); p.put(26, 17, CREAM);                                       // car number plate
  for (let x = 0; x < CAR_W; x++) { p.put(x, 19, MARD); p.put(x, 20, '#4a3226'); }
  for (const sx of [4, 9]) { p.put(sx, 6, '#2a1c14'); p.put(sx, 7, SKIN); for (let y = 8; y <= 19; y++) p.put(sx, y, y < 14 ? '#46384a' : '#26202c'); p.put(sx + 1, 9, SKIN); }  // riders on the running board
  for (const [a, b] of [[5, 13], [32, 40]]) for (let x = a; x <= b; x++) { p.put(x, 21, '#15151c'); p.put(x, 22, (x - a) % 4 === 1 ? '#6a6a7a' : '#22222c'); }
  p.put(0, 14, '#fff4d0'); p.put(0, 15, '#ffd890'); p.put(CAR_W - 1, 16, '#ff4030');                       // headlamp, tail lamp
  return p.done();
}
function shearCar(base) {                          // four sub-pixel phases so the wheels always sit on the rails
  const out = [];
  for (let ph = 0; ph < 4; ph++) {
    const [c, x] = makeCanvas(CAR_W, CAR_H + 12);
    for (let i = 0; i < CAR_W; i++) x.drawImage(base, i, 0, 1, CAR_H, i, Math.floor((ph + i) / 4), 1, CAR_H);
    out.push(c);
  }
  return out;
}
function renderCarBeam(dir) {                      // headlamp beam along the grade
  const w = 64, h = 26, p = painter(w, h), oy = dir < 0 ? 18 : 6;
  for (let d = 0; d < w; d++) {
    const cy = oy + (dir < 0 ? -d / 4 : d / 4), th = 0.8 + d * 0.11;
    for (let j = 0; j < h; j++) {
      const e = 1 - Math.abs(j - cy) / th;
      if (e <= 0) continue;
      const q = Math.floor(0.22 * Math.pow(1 - d / w, 1.3) * Math.min(1, e * 1.5) * 30 + bayer(d, j)) / 30;
      if (q > 0) p.put(dir < 0 ? w - 1 - d : d, j, '#fff0c8', Math.round(q * 255));
    }
  }
  return p.done();
}
const CAR_UP = { t0: 22, ts: 50, te: 59, t1: 80, x0: 300, xs: 64, x1: -52 };
const CAR_DOWN = { t0: 145, t1: 181, x0: -52, x1: 300 };
const BELLS_UP = [24, 24.45, 50.3, 59.2, 59.65, 69], BELLS_DOWN = [146, 146.45, 164];
function carUpX(t) {
  const c = CAR_UP;
  if (t < c.t0 || t >= c.t1) return null;
  if (t < c.ts) return Math.round(lerp(c.x0, c.xs, cruise((t - c.t0) / (c.ts - c.t0), 0, 0.24)));
  if (t < c.te) return c.xs;
  return Math.round(lerp(c.xs, c.x1, cruise((t - c.te) / (c.t1 - c.te), 0.3, 0)));
}
function carDownX(t) {
  const c = CAR_DOWN;
  return t < c.t0 || t >= c.t1 ? null : Math.round(lerp(c.x0, c.x1, (t - c.t0) / (c.t1 - c.t0)));
}
function drawCar(t, X, dir, rail, bells) {
  const ph = ((X % 4) + 4) % 4, spr = CARS[dir > 0 ? 1 : 0][ph], y = S0 + rail + Math.floor(X / 4) - (CAR_H - 1);
  const off = i => Math.floor((ph + i) / 4);
  halo(HL.carGlow, X + 26, y + 26 + off(26), 0.8);                          // warm spill on the wet street
  ctx.drawImage(spr, X, y);
  const fi = dir < 0 ? 0 : CAR_W - 1, hx = X + fi, hy = y + 14 + off(fi);
  ctx.globalCompositeOperation = 'lighter';
  ctx.drawImage(dir < 0 ? BEAM_UP : BEAM_DN, dir < 0 ? hx - 63 : hx, dir < 0 ? hy - 18 : hy - 6);
  ctx.globalCompositeOperation = 'source-over';
  halo(HL.carHead, hx, hy, 0.95);
  halo(HL.carWin, X + 26, y + 9 + off(26), 0.75);
  const ti = dir < 0 ? CAR_W - 1 : 0; halo(HL.tail, X + ti, y + 16 + off(ti), 0.7);
  const bi = dir < 0 ? 13 : CAR_W - 14;                                       // the bell flashes when it rings
  for (const tb of bells) { const dt = t - tb; if (dt >= 0 && dt < 0.8) halo(HL.bell, X + bi, y + off(bi), Math.exp(-dt * 5)); }
  return { hx, hy };
}

/* ================= Houses per frame: lit rooms, a walker, a cat, a TV, porch lanterns, chimney smoke ================= */
function roomOn(rm, t) {
  if (rm.kind !== 'toggle') return true;
  return hash(rm.id + 80, Math.floor(frac(t / LOOP + rm.off) * rm.n), 4) < 0.62;
}
function drawHouses(t) {
  ctx.drawImage(HOUSES_DARK.c, HOUSES_DARK.x, HOUSES_DARK.y);
  for (const rm of ROOMS) {
    if (!roomOn(rm, t)) continue;
    for (const [x, y, w, h] of rm.rects) ctx.drawImage(HOUSES_LIT, x, y, w, h, x, y, w, h);
    const [x, y, w, h] = rm.rects[0], SIL = '#4a2a1c';
    if (rm.kind === 'person') {                                  // someone pacing past the bay window
      const bx = x + 6 + Math.round(3.5 + 3.5 * wave(t, 5, 0.2));
      rect(ctx, bx + 1, y + 2, 2, 2, SIL); rect(ctx, bx, y + 4, 4, h - 4, SIL); px(ctx, bx + 1, y + 1, SIL);
    } else if (rm.kind === 'cat') {                              // a cat on the sill, tail swishing
      const C = '#2a1810', sy = y + h - 1, tail = Math.round(1.5 * wave(t, 30, 0.1));
      rect(ctx, x + 8, sy - 2, 5, 2, C); rect(ctx, x + 12, sy - 4, 2, 2, C); px(ctx, x + 12, sy - 5, C); px(ctx, x + 13, sy - 5, C);
      px(ctx, x + 7, sy - 1, C); px(ctx, x + 6 + tail, sy - 2, C); px(ctx, x + 6 + tail, sy - 3, C);
    } else if (rm.kind === 'tv') {                               // blue television flicker
      ctx.globalAlpha = 0.25 + 0.35 * hash(rm.id, step(t, 1200), 7);
      rect(ctx, x, y, w, h, '#6a9aff'); ctx.globalAlpha = 1;
    }
    halo(rm.kind === 'tv' ? HL.tv : HL.win, x + (w >> 1), y + (h >> 1), rm.key.startsWith('bay') ? 0.75 : 0.5);
  }
  PORCH.forEach(([x, y], i) => halo(HL.porch, x, y, 0.85 + 0.15 * wave(t, 900 + i * 37)));
  for (const s of SMOKE) {                                        // chimney smoke drifting toward the bay
    const u = frac(t * s.n / LOOP + s.ph);
    const x = CHIMNEY.x + 2 + u * u * 34 + u * 8 + wave(t, s.k, s.p) * 1.5 * u, y = CHIMNEY.y - u * 26 + u * u * 6;
    const rr = Math.min(5, 1 + Math.floor(u * 5));
    ctx.globalAlpha = 0.4 * Math.min(1, u * 7) * Math.pow(1 - u, 1.2);
    ctx.drawImage(SMOKE_P[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
const SMOKE = [];
{ const r = rng(61); for (let i = 0; i < 14; i++) SMOKE.push({ n: 26 + Math.floor(r() * 8), ph: r(), k: 18 + Math.floor(r() * 18), p: r() }); }
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
function renderProps() {                          // a white fire hydrant with a blue bonnet on the near sidewalk
  const p = painter(W, H), X = 30, B = sw(X) + 35;
  const rows = ['..bbb..', '.bbbbb.', '..www..', 'nwwwwsn', '..wws..', '..wws..', '.wwwws.', 'kkkkkkk'];
  rows.forEach((row, j) => { for (let i = 0; i < 7; i++) {
    const col = { b: '#3a5aa8', w: '#c4c8d6', s: '#7a7e92', n: '#9aa0b4', k: '#2a2a36' }[row[i]]; if (col) p.put(X - 3 + i, B - 8 + j, col); } });
  for (let i = -3; i <= 4; i++) p.put(X + i, B, '#15151e');
  return p.done();
}

/* ================= Someone walking a dog up the near sidewalk, leaning into the grade ================= */
const WALKER = { t0: 94, t1: 146, x0: 206, x1: -18 };
function drawWalker(t) {
  if (t < WALKER.t0 || t >= WALKER.t1) return;
  const u = (t - WALKER.t0) / (WALKER.t1 - WALKER.t0), x = Math.round(lerp(WALKER.x0, WALKER.x1, u)), fy = sw(x) + 37;
  const fr = step(t, 432) & 1, warm = Math.abs(x - LAMP.x) < 44;
  const COAT = '#3a3446', EDGE = warm ? '#b07a4a' : '#5a5470', SKIN = '#d2a07c';
  if (fr) { rect(ctx, x + 1, fy - 3, 1, 3, '#1a1822'); rect(ctx, x + 3, fy - 3, 1, 3, '#15141c'); }       // legs, striding uphill
  else { rect(ctx, x + 2, fy - 3, 1, 3, '#1a1822'); rect(ctx, x + 2, fy - 3, 2, 1, '#15141c'); px(ctx, x + 3, fy - 1, '#15141c'); }
  rect(ctx, x, fy - 10, 4, 7, COAT); rect(ctx, x, fy - 10, 1, 7, EDGE); rect(ctx, x + 1, fy - 11, 3, 1, '#8a2a34');   // coat + scarf
  rect(ctx, x, fy - 14, 3, 3, SKIN); rect(ctx, x, fy - 15, 4, 2, '#2a2a3a'); px(ctx, x + 3, fy - 13, '#2a2a3a');       // head, beanie
  px(ctx, x - 1, fy - 7, SKIN);                                                                                      // hand
  const dx = x - 9, dy = sw(dx) + 37, dfr = step(t, 720) & 1;                                                         // the dog, pulling ahead
  rect(ctx, dx, dy - 4, 5, 2, '#6a4a32'); rect(ctx, dx - 2, dy - 6, 2, 2, '#6a4a32'); px(ctx, dx - 3, dy - 5, '#3a2a1c');
  px(ctx, dx + 4, dy - 5, '#6a4a32'); px(ctx, dx + 5, dy - 6 + dfr, '#6a4a32');
  for (const lx of dfr ? [0, 3] : [1, 4]) rect(ctx, dx + lx, dy - 2, 1, 2, '#4a3222');
  line(ctx, x - 1, fy - 7, dx + 1, dy - 5, '#8a3a3a');
  if (warm) halo(HL.porch, x + 1, fy - 8, 0.3);
}

/* ================= A couple strolling down toward the view: they stop, she points out the bridge, they walk on ================= */
const STROLL = { t0: 160, ts: 185, te: 194, t1: 230, x0: -14, xs: 96, x1: 256 };   // in from the left, out past the bottom edge
// k: feet below sw(x), nearer than the hydrant so they pass in front of it; edges: the moonlit and the lamplit shoulder line
const STROLLERS = [
  { dx: 0, k: 38, P: { legs: 4, body: 8, coat: '#6e5442', trousers: '#0f0e14', skin: '#c89474', hair: '#2a1c18', long: true } },
  { dx: -6, k: 42, P: { legs: 5, body: 8, coat: '#2a3a36', trousers: '#3a4868', skin: '#a8764e', hair: '#1a1618', long: false } },
].map(s => ({ ...s, cool: tint(s.P.coat, '#9aa6c6', 0.35), warm: tint(s.P.coat, '#ffb466', 0.5) }));
function strollX(t) {
  const s = STROLL;
  if (t < s.ts) return Math.round(lerp(s.x0, s.xs, cruise((t - s.t0) / (s.ts - s.t0), 0, 0.15)));
  if (t < s.te) return s.xs;
  return Math.round(lerp(s.xs, s.x1, cruise((t - s.te) / (s.t1 - s.te), 0.15, 0)));
}
function drawStroller(x, fy, fr, P, edge, pose) {           // in profile, facing downhill (right); pose: walk, stand, point
  const top = fy - P.legs - P.body;
  if (fr) { rect(ctx, x, fy - P.legs, 1, P.legs, P.trousers); rect(ctx, x + 2, fy - P.legs, 1, P.legs - 1, P.trousers); px(ctx, x + 3, fy - 1, P.trousers); }
  else rect(ctx, x + 1, fy - P.legs, 2, P.legs, P.trousers);
  rect(ctx, x, top, 4, P.body, P.coat); rect(ctx, x, top, 4, 1, edge); rect(ctx, x + 3, top + 1, 1, P.body - 1, edge);
  rect(ctx, x + 1, top - 3, 3, 3, P.skin); rect(ctx, x, top - 4, 4, 1, P.hair); rect(ctx, x, top - 3, 1, P.long ? 5 : 2, P.hair);
  if (pose === 'point') { px(ctx, x + 4, top + 1, P.coat); px(ctx, x + 5, top, P.coat); px(ctx, x + 6, top - 1, P.skin); }   // arm up toward the bridge
  else if (pose === 'walk') px(ctx, fr ? x + 4 : x - 1, top + 4, P.skin);                                                  // a swinging hand
}
function drawStrollers(t) {
  if (t < STROLL.t0 || t >= STROLL.t1) return;
  const X = strollX(t), still = t >= STROLL.ts && t < STROLL.te, fr = step(t, 480) & 1;
  const point = t >= STROLL.ts + 1.5 && t < STROLL.te - 2;
  STROLLERS.forEach((s, i) => {                                // the farther one first; the nearer one half a stride behind
    const x = X + s.dx, fy = sw(x) + s.k, warm = Math.abs(x - LAMP.x) < 44;
    drawStroller(x, fy, still ? 0 : fr ^ i, s.P, warm ? s.warm : s.cool, !still ? 'walk' : point && i === 0 ? 'point' : 'stand');
    if (warm) halo(HL.porch, x + 2, fy - 9, 0.3);
  });
}

/* ================= Fine drizzle at three depths, glinting only where the light catches it ================= */
const DRIZ = [
  { count: 120, nMin: 22, nMax: 30, fall: 60, len: 1, slope: 0.35, a: 0.2, x0: 176, x1: W, y0: 100, y1: 262, sway: 1.5 },
  { count: 180, nMin: 38, nMax: 50, fall: 96, len: 2, slope: 0.4, a: 0.24, x0: -20, x1: W, y0: 40, y1: 272, sway: 2.5 },
  { count: 72, nMin: 60, nMax: 72, fall: 140, len: 3, slope: 0.45, a: 0.3, x0: -30, x1: W, y0: 0, y1: 280, sway: 4 },
];
DRIZ.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 8 + Math.floor(r() * 10), p: r() });
});
let LIGHTS = [], LIGHTS_BB = [];                  // lights that catch the drizzle, without / with the billboard's
// a picked weather (site/js/engine/weather.js): San Francisco draws its own fine drizzle, so Rain gets the heavier shared
// rain; it lands in the lower city past the wall, on the street and past the bottom of the frame, and glints in the
// same lights as the drizzle (built with them in prerender), cable-car headlamps included. The fog bank, the cascade,
// the tongue and the wisps are not falling weather and stay in every pick
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[WF + 3, 246], [236, 266], [H + 4, H + 4]];
const wxLight = l => { const r = Math.sqrt(l.r2); return { x: l.x, y: l.y, rx: r, ry: r, col: '#ffe0a8' }; };
let WX, WX_BB;
function drawDrizzle(L, t, base, extra) {
  const lights = extra ? base.concat(extra) : base, sp = step(t, 1920);
  for (const d of L.drops) {
    const { cyc, u } = cycle(t, d.n, d.ph);
    const xs = L.x0 + hash(d.id, cyc, 11) * (L.x1 - L.x0), ys = L.y0 + hash(d.id, cyc, 12) * (L.y1 - L.y0 - L.fall);
    const y = ys + u * L.fall, x = xs + u * L.fall * L.slope + L.sway * wave(t, d.k, d.p);
    let lf = 0;
    for (const l of lights) { const dx = x - l.x, dy = y - l.y, q = 1 - (dx * dx + dy * dy) / l.r2; if (q > lf) lf = q * l.s; }
    const fade = Math.sin(Math.PI * u);
    if (lf > 0.02) {
      const sparkle = hash(d.id, sp, 5) < 0.12;
      ctx.fillStyle = sparkle ? '#ffffff' : '#ffe0a8';
      ctx.globalAlpha = fade * Math.min(1, 0.25 + lf * (sparkle ? 1.2 : 0.8));
    } else { ctx.fillStyle = '#aab4d8'; ctx.globalAlpha = fade * L.a; }
    const X = Math.round(x), Y = Math.round(y), n = L.len + (lf > 0.3 ? 1 : 0);
    for (let k = 0; k < n; k++) ctx.fillRect(Math.round(X - k * L.slope), Y - k, 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Fog wisps drifting up the street, warming as they pass the lamp ================= */
const WISP_SIZES = [[84, 11], [116, 14], [66, 10], [98, 13]];
function renderWisp(w, h, col, seed) {
  const p = painter(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const dx = (x - w / 2) / (w / 2), dy = (y - h / 2) / (h / 2);
    let v = (1 - dx * dx - dy * dy * 1.2) * (0.62 + 0.24 * Math.sin(x * 0.21 + seed + y * 0.4) + 0.14 * Math.sin(x * 0.53 - seed * 2));
    const q = Math.floor(clamp(v, 0, 1) * 5 + bayer(x, y)) / 5;
    if (q > 0) p.put(x, y, col, Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
const WISPS = [
  { s: 0, y: 160, k: 2, ph: 0.1, a: 0.46 }, { s: 1, y: 186, k: 1, ph: 0.45, a: 0.42 }, { s: 2, y: 210, k: 3, ph: 0.72, a: 0.44 },
  { s: 3, y: 140, k: 2, ph: 0.32, a: 0.36 }, { s: 1, y: 228, k: 2, ph: 0.86, a: 0.34 }, { s: 0, y: 176, k: 3, ph: 0.57, a: 0.4 },
];
function drawWisps(t) {
  for (const w of WISPS) {
    const [ww, wh] = WISP_SIZES[w.s], x = Math.round(frac(w.k * t / LOOP + w.ph) * (W + 200)) - 140;
    const y = w.y + Math.round(2 * wave(t, 5, w.ph)), cx = x + ww / 2, cy = y + wh / 2;
    const warm = clamp(1 - Math.hypot((cx - LAMP.x) * 0.8, (cy - LAMP.y) * 1.5) / 80, 0, 1);
    ctx.globalAlpha = w.a * (1 - warm * 0.6); ctx.drawImage(WISP_C[w.s][0], x, y);
    if (warm > 0.01) {
      ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, w.a * warm * 1.3);
      ctx.drawImage(WISP_C[w.s][1], x, y); ctx.globalCompositeOperation = 'source-over';
    }
  }
  ctx.globalAlpha = 1;
}

function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 2; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, TAM_C, BANKS, FOG_BODY, MARIN_MASK, MARIN_SCR, HEAD_C, CAS_TEX, CAS_MASK, CAS_SCR, WATER_C, CABLES_C, DECK_C, TOWERS_C,
  SHIP_C, TONGUE_A, TONGUE_B, TONGUE_M, TG_SCR, ALC_C, BEAMS, BOAT_C, WATERFRONT_C, CITY_BACK, CITY_FRONT,
  HOUSES_DARK, HOUSES_LIT, STREET_C, CONE_C, CARS, BEAM_UP, BEAM_DN, PROPS_C, SMOKE_P, WISP_C, VIG_C,
  BILL_C, POSTERS, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(48, '#8ea8e8', 0.2, 1.8), moonCore: makeHalo(15, '#e6ecff', 0.32, 1.5),
    head: makeHalo(7, '#fff4d0', 0.5, 1.2), strobe: makeHalo(5, '#ffffff', 0.7, 1), red: makeHalo(4, '#ff3030', 0.6, 1),
    redDim: makeHalo(3, '#ff4030', 0.4, 1), tower: makeHalo(38, '#ff8a4a', 0.16, 1.6), towerTop: makeHalo(18, '#ff9a5a', 0.1, 1.6),
    deckLamp: makeHalo(5, '#ffd08a', 0.4, 1.2), mast: makeHalo(4, '#ffffff', 0.5, 1), shipDeck: makeHalo(12, '#ffc27a', 0.2),
    horn: makeHalo(48, '#ffd6a8', 0.2, 1.4), hornCore: makeHalo(9, '#fff0c8', 0.5, 1.2),
    lh: makeHalo(8, '#fff0c0', 0.5, 1.2), lhFlash: makeHalo(30, '#fff4d8', 0.35, 1.4), cabin: makeHalo(5, '#ffc870', 0.4, 1),
    shipGlow: makeHalo(20, '#ffc070', 0.1, 1.4), pierLamp: makeHalo(5, '#ffd08a', 0.4, 1.2), streetLamp: makeHalo(5, '#ffc070', 0.3, 1.2),
    win: makeHalo(14, '#ffb45a', 0.16), tv: makeHalo(14, '#6a9aff', 0.2), porch: makeHalo(12, '#ffc878', 0.34, 1.4),
    lamp: makeHalo(66, '#ffb060', 0.24, 1.7), lampCore: makeHalo(16, '#ffe6b0', 0.55, 1.4), carHead: makeHalo(9, '#fff4d0', 0.6, 1.2),
    carWin: makeHalo(26, '#ffb860', 0.16, 1.5), carGlow: makeHalo(24, '#ffa050', 0.1, 1.4), tail: makeHalo(4, '#ff3020', 0.5, 1.2),
    bell: makeHalo(7, '#fff0a0', 0.7, 1.2),
    billGlow: makeHalo(28, '#ffc878', 0.08, 1.6), billLamp: makeHalo(4, '#ffd894', 0.3, 1.3),   // small lamps: under the towers' glow
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); TAM_C = trim(renderTam());
  BANKS = BANK_DEFS.map(makeBankLayer); FOG_BODY = renderFogBody(); MARIN_MASK = renderMarinMask(); MARIN_SCR = makeCanvas(W - 340, BANK_DEFS[2].h);
  HEAD_C = trim(renderHeadlands());
  CAS_TEX = renderCascadeTex(); CAS_MASK = renderCascadeMask(); CAS_SCR = makeCanvas(CAS.w, CAS.h);
  WATER_C = renderWater();
  CABLES_C = trim(renderCables()); DECK_C = trim(renderDeck()); TOWERS_C = trim(renderTowers());
  SHIP_C = renderShip();
  TONGUE_A = renderTongueTex(0.4); TONGUE_B = renderTongueTex(2.1); TONGUE_M = renderTongueMask(); TG_SCR = makeCanvas(TG.w, TG.h);
  ALC_C = renderAlcatraz();
  BEAMS = [0, 1].map(dir => Array.from({ length: BEAM_LV }, (_, k) => renderBeam(Math.round(BEAM_L * k / (BEAM_LV - 1)), dir)));
  BOAT_C = [renderSailboat(), renderMotorboat()];
  WATERFRONT_C = trim(renderWaterfront()); CITY_BACK = trim(renderCityBack()); CITY_FRONT = trim(renderCityFront());
  HOUSES_DARK = trim(renderHouses(false)); HOUSES_LIT = renderHouses(true);
  BILL_C = trim(renderBillboard());
  POSTERS = [false, true].map(dim => Array.from({ length: 16 }, (_, s) => renderPoster(dim, s >> 2, s & 3)));
  PITCH = makePitch(BILL.w, BILL.h, { accent: '#ff6a3d' });
  STREET_C = trim(renderStreet()); CONE_C = renderCone();
  const car = renderCarBase(5);
  CARS = [shearCar(car), shearCar(flipH(car))];
  BEAM_UP = renderCarBeam(-1); BEAM_DN = renderCarBeam(1);
  PROPS_C = trim(renderProps());
  SMOKE_P = [null]; for (let r = 1; r <= 5; r++) SMOKE_P.push(makePuff(r, '#8a8ea6', '#646880'));
  WISP_C = WISP_SIZES.map(([w, h], i) => [renderWisp(w, h, '#9aa6c6', i * 1.7), renderWisp(w, h, '#ffc890', i * 1.7)]);
  VIG_C = renderVignette();
  LIGHTS = [{ x: LAMP.x, y: LAMP.y, r: 56, s: 1 }, ...PORCH.map(([x, y]) => ({ x, y, r: 16, s: 0.8 })),
    { x: T0.cx, y: DECK - 12, r: 30, s: 0.5 }, { x: T1.cx, y: DECK - 12, r: 26, s: 0.45 }].map(l => ({ ...l, r2: l.r * l.r }));
  LIGHTS_BB = [...LIGHTS, { x: BILL.x + (BILL.w >> 1), y: BILL.y + BILL.h - 2, r2: 22 * 22, s: 0.3 }];
  WX = { land: WX_LAND, lights: LIGHTS.map(wxLight) }; WX_BB = { land: WX_LAND, lights: LIGHTS_BB.map(wxLight) };
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves it out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const lights = billboard ? LIGHTS_BB : LIGHTS, wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  blit(TAM_C);
  drawBank(t);
  drawWater(t);
  blit(HEAD_C);
  MARIN_LIGHTS.forEach(([x, y, k], i) => {
    if (k === 2) { const on = frac(t / 3) < 0.25; px(ctx, x, y, on ? '#5aff8a' : '#1a4a2a'); if (on) halo(HL.cabin, x, y, 0.5); return; }
    ctx.globalAlpha = 0.55 + 0.35 * wave(t, 40 + i * 7, i * 0.13); px(ctx, x, y, k ? '#c8d8ff' : '#ffd08a'); ctx.globalAlpha = 1;
  });
  drawCascade(t);
  drawWaterDetails(t);
  drawBridge(t);
  drawTongue(t);
  drawDeckLamps(t);
  drawFoghorn(t);
  drawBoats(t, true);
  drawAlcatraz(t);
  drawShip(t);
  drawBoats(t, false);
  drawWaterfront(t);
  drawCity(t);
  if (wx.drizzle) drawDrizzle(DRIZ[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  if (billboard) drawBillboard(t, pitch);
  drawHouses(t);
  blit(STREET_C);
  const up = carUpX(t), dn = carDownX(t), li = lampI(t), carLights = [];
  if (up !== null) { const h = drawCar(t, up, -1, RAIL_F, BELLS_UP); carLights.push({ x: h.hx - 20, y: h.hy - 5, r2: 900, s: 0.9 }); }
  if (dn !== null) { const h = drawCar(t, dn, 1, RAIL_N, BELLS_DOWN); carLights.push({ x: h.hx + 20, y: h.hy + 5, r2: 900, s: 0.9 }); }
  blit(PROPS_C);
  drawWalker(t);
  drawStrollers(t);
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = li;
  ctx.drawImage(CONE_C, LAMP.x - 55 + 1, LAMP.y + 3);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  if (li < 0.9) rect(ctx, LAMP.x - 2, 184, 6, 5, `rgba(40,24,10,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x + 1, LAMP.y, li); halo(HL.lampCore, LAMP.x + 1, LAMP.y, li);
  const wxn = carLights.length ? { ...wxc, lights: wxc.lights.concat(carLights.map(wxLight)) } : wxc;   // + headlamps
  if (wx.drizzle) drawDrizzle(DRIZ[1], t, lights, carLights);
  drawWeather(ctx, wx.shared, 1, t, wxn, reducedMotion);
  drawWisps(t);
  if (wx.drizzle) drawDrizzle(DRIZ[2], t, lights, carLights);
  drawWeather(ctx, wx.shared, 2, t, wxn, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'san-francisco',
  name: 'San Francisco',
  tagline: 'Fog over the Golden Gate',
  timeZone: 'America/Los_Angeles',
  accent: '#ff6a3d',
  focusX: Math.round((T0.cx + T1.cx) / 2),
  landmark: [T0.cx - T0.hw - 1, T1.cx + T1.hw + 1],    // both towers of the Golden Gate
  landmarkTop: T0.top - 1,                             // the south tower's beacons, which landscape crops keep
  billboard: BILL,
  ambience: { wind: 0.35, water: 0.3, traffic: 0.2, rain: 0.1 },
  events: [
    ...FOGHORNS.map(t => ({ t, type: 'horn', x: T0.cx, gain: 0.55 })),
    { t: SHIP.t0, type: 'boat', dur: SHIP.t1 - SHIP.t0 - 10, pan: [1, 0.05], gain: 0.45 },
    { t: SHIP_HORN, type: 'horn', x: 368, gain: 0.7 },
    ...BELLS_UP.map(t => ({ t, type: 'bell', x: 90, gain: 0.35 })),
    ...BELLS_DOWN.map(t => ({ t, type: 'bell', x: 120, gain: 0.3 })),
    { t: LION_DIVE.t, type: 'splash', x: 464, gain: 0.3 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
